import { useEffect, useState, useId, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, AreaChart, Area, CartesianGrid, Legend, LabelList,
} from "recharts";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar, { DATE_PRESETS_EOD } from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useEodSummaryData } from "../../hooks/useEodSummaryData";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { exportPeriodFor, resolveTokenRange } from "../../utils/reportPeriod";
import { useBranch } from "../../context/BranchContext";
import { useAuth } from "../../context/AuthContext";
import { getEodTaxesData, getEodTaxDiscrepancy, todayStr, isTodayRange } from "../../services/reportsApi";
import { getCurrencySymbol, formatMoneyForExport } from "../../utils/currency";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { formatMonetaryExport, escapeCsvCell, CSV_BOM } from "../../utils/reportExport";
import { isNativePlatform, exportTablePdfNative, exportTableXlsxNative } from "../../utils/capacitorExport";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import KpiTooltip from '../KpiTooltip';
import "./reports.css";
import "./EODSummaryReport.scss";
import { T, useIsDesktop, Bone } from "./reportTokens";
import { TipsGratuityDrill, buildTipsGratuityData, TG_CARD_LABEL, TG_DRILL_TITLE } from "./components/TipsGratuity";
import SalesWaterfall, { type WaterfallSource, buildWaterfallData } from "./components/SalesWaterfall";
import { waterfallRefundContextFromSnapshot, REFUND_KPI_TOOLTIP } from "./refunds/refundModel";
import { refundsPath } from "./refunds/refundsPath";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchLiveVoidSummaryRequest, fetchLiveCompOrderLogRequest } from "../../redux/reports/clickHouse/clickHouseActions";
import { fetchEodSummaryReportRequest, fetchEodFundsReportRequest } from "../../redux/reports/EODClickhouse/eodClickhouseActions";
import { getEodSummaryReportApi } from "../../redux/reports/EODClickhouse/eodClickhouseApi";
import type { EodSummaryReportRow, EodSummaryTableRow, EodFundsTableRow } from "../../interface/eodClickhouseInterface";
import { EOD_SUMMARY_DEFAULT_PAGE, EOD_SUMMARY_DEFAULT_SORT_ASC, EOD_PAGE_SIZE_DESKTOP, EOD_PAGE_SIZE_MOBILE, EOD_FUNDS_SORT_BY } from "../../interface/eodClickhouseInterface";
import { ReportTablePager } from "./components/ReportTablePager";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import SalesByChannelBreakdown from "./SalesByChannelBreakdown";
import { useSalesByChannelBreakdown } from "../../hooks/useSalesByChannelBreakdown";
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { getLivePaymentsApi } from "../../redux/reports/clickHouse/clickHouseApi";
import type { OrderSource } from "../../redux/reports/clickHouse/clickHouseApi";

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — EOD SUMMARY REPORT
   Single responsive component: shared EODContent for desktop + mobile.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Global sales-channel filter — the same three options, labels and mapping Snapshot and
 * Sales use. "Overall sales" sends NO `orderSource` param at all (never '' or 'overall').
 *
 * Per-report state: EOD owns its own selection, defaults to Overall on a fresh visit and
 * does not bleed across to Snapshot or Sales. It rides only on the ClickHouse
 * `/sales/live/v2/*` calls — ALL of them, by-channel included (EODCH-CHANNEL-2). The Taxes
 * table's endpoint moved under `/sales/live/v2/` too (`getEodTaxesData` →
 * `/sales/live/v2/getTaxesData`) but doesn't send `orderSource` yet — untouched by that
 * migration, not yet wired into the channel filter. `getEodTaxDiscrepancy`
 * (`/eod/getTaxDiscrepancy`) is EOD's one remaining legacy REST feed and never receives it.
 *
 * NOTE: Snapshot and Sales each declare their own copy of these two — this is a third.
 * Worth folding into one shared module the next time all three are touched together.
 */
const CHANNEL_OPTIONS = ['Overall sales', 'Direct sales', '3rd party sales'];
const channelToOrderSource = (c: string): OrderSource | undefined =>
  c === 'Direct sales' ? 'direct' : c === '3rd party sales' ? 'thirdparty' : undefined;

/**
 * Drill-downs KEEP the channel control — a drill inherits the parent report's selection,
 * and switching channel while inside one refetches and redraws it in place.
 *
 * The exceptions are the drills whose parent section does not exist under "3rd party
 * sales" — Tips & Gratuity (its card is hidden and the KPI row reflows to three) and Tax
 * Discrepancy (the whole Taxes table is hidden). Offering the switch inside those would let
 * the user strand themselves in a drill whose parent is gone the moment they navigate back.
 * Snapshot carves out its own three 3rd-party-hidden KPIs' drills (Active Staff / Voids /
 * Complimentary) for exactly this reason.
 *
 * NOTE: the Tax Discrepancy drill DOES show the control, but its content is fed by a legacy
 * REST endpoint (`/eod/getTaxDiscrepancy`) that cannot take `orderSource`, so its numbers
 * will not move when it is switched. It is the last of the three that had this gap —
 * Tender Types (EODCH-TENDER) and Channels (EODCH-CHANNEL) are both migrated, and every
 * ClickHouse call on the screen now carries the filter (EODCH-CHANNEL-2).
 */
const CHANNEL_HIDDEN_DRILLS = ['tips', 'taxDiscrepancy'];

function EODDesktopShimmer() {
  const kpiLabels = ["Total Collected", "Net Sales", "Total Refunds", TG_CARD_LABEL];
  return (
    <div className="rpt-page">
      {/* KPI Row */}
      <div className="rpt-flex-row rpt-gap-16">
        {kpiLabels.map((label) => (
          <div key={label} className="rpt-tile eod-shimmer-kpi">
            <div className="rpt-tile__label eod-shimmer-kpi__label">{label}</div>
            <Bone h={32} r={6} mb={6} />
            <Bone h={14} w={90} r={4} />
          </div>
        ))}
      </div>
      {/* Row: Tender Mix + Channel Revenue */}
      <div className="rpt-flex-row rpt-gap-16">
        <div className="rpt-card rpt-card--r16 eod-shimmer-tender">
          <div className="rpt-section-title">Tender Mix</div>
          <Bone h={140} r={8} />
        </div>
        <div className="rpt-card rpt-card--r16 eod-shimmer-channel">
          <div className="rpt-section-title">Channel Revenue</div>
          <Bone h={140} r={8} />
        </div>
      </div>
      {/* Funds Flow */}
      <div className="rpt-card rpt-card--r16">
        <div className="rpt-section-title">Funds Flow</div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="rpt-flex-row rpt-gap-12 eod-shimmer-funds-row">
            <Bone w={80} h={14} r={4} />
            <Bone w={70} h={14} r={4} />
            <Bone w={70} h={14} r={4} />
            <Bone w={70} h={14} r={4} />
          </div>
        ))}
      </div>
    </div>
  );
}

function EODMobileShimmer() {
  const kpiLabels = ["Total Collected", "Net Sales", "Total Refunds", TG_CARD_LABEL];
  return (
    <div className="eod-mobile-shimmer">
      {/* KPI grid 2×2 */}
      <div className="eod-mobile-shimmer__grid">
        {kpiLabels.map((label) => (
          <div key={label} className="rpt-tile rpt-tile--compact eod-mobile-shimmer__kpi">
            <div className="rpt-card-label-sm eod-mobile-shimmer__kpi-label">{label}</div>
            <Bone h={24} r={5} mb={4} />
            <Bone h={12} w={60} r={4} />
          </div>
        ))}
      </div>
      {/* Tender Mix */}
      <div className="rpt-card rpt-card--r16 eod-mobile-shimmer__chart-card">
        <div className="rpt-section-title">Tender Mix</div>
        <Bone h={100} r={8} />
      </div>
      {/* Channel Revenue */}
      <div className="rpt-card rpt-card--r16 eod-mobile-shimmer__chart-card">
        <div className="rpt-section-title">Channel Revenue</div>
        <Bone h={100} r={8} />
      </div>
    </div>
  );
}

/* -- Inline Table Shimmer (section-level, within a card) -- */
function EodTableShimmer({ cols = 3, rows = 6 }: { cols?: number; rows?: number }) {
  return (
    <div style={{ padding: '4px 0' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rpt-flex-row rpt-gap-12" style={{ padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
          <Bone w={80} h={13} r={4} />
          {Array.from({ length: cols - 1 }).map((_, j) => (
            <Bone key={j} w={null} h={13} r={4} style={{ flex: 1 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

const buildKpiItemsDefault = (hideTipsGratuity = false) => {
  const zero = `${getCurrencySymbol()}0`;
  return [
    { label: "Total Collected", value: zero, icon: "trending_up", iconColor: T.green, blurColor: T.greenSoft, tooltip: "Revenue deducting discounts, complimentary, voids & refunds. The amount paid by customers including tax, tips & other fees" },
    { label: "Net Sales", value: zero, icon: "account_balance_wallet", iconColor: T.accent, blurColor: T.accentSoft, tooltip: "From Total collected after deducting tax, tips & gratuity. The revenue retained by you after all deductions." },
    { label: "Total Refunds", value: zero, drillKey: "refunds", icon: "undo", iconColor: T.red, blurColor: T.redSoft, tooltip: REFUND_KPI_TOOLTIP },
    ...(hideTipsGratuity ? [] : [{ label: TG_CARD_LABEL, value: zero, drillKey: "tips", icon: "volunteer_activism", iconColor: T.green, blurColor: T.greenSoft, cardBg: T.greenSoft }]),
  ];
};

const fmt = (n: any) =>
  (n < 0 ? "-" : "") + getCurrencySymbol() + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0 });
const fmt2 = (n: any) => n.toLocaleString("en-US", { minimumFractionDigits: 2 });

/**
 * ZAR-1762 / TG-2 — EOD's Tips & Gratuity data, in the shared drill-in contract.
 *
 * Reads `data.tipsGratuity`, which useEodSummaryData builds from the same three ClickHouse
 * endpoints — and the same response keys — that Snapshot and Sales use. The only difference is
 * `orderSource: 'direct'`, which is what TG-1 defines EOD as:
 *   snapshot-details → tip, gratuity, orderCount, ordersWithTips, ordersWithGratuity
 *   by-channel       → channel + tip / gratuity   (both channel panels)
 *   staff-perfomance → staff_name + tip / gratuity (both staff rankings)
 */
const buildEodTipsGratuityData = (data: any) => {
  const tg = data?.tipsGratuity;
  return buildTipsGratuityData({
    staff: tg?.staff ?? [],
    channels: tg?.channels ?? [],
    netSales: Number(tg?.netSales) || 0,
    tipsTotal: Number(tg?.totalTips) || 0,
    gratuityTotal: Number(tg?.gratuity) || 0,
    totalOrders: Number(tg?.totalOrders) || 0,
    ordersWithTips: Number(tg?.ordersWithTips) || 0,
    ordersWithGratuity: Number(tg?.ordersWithGratuity) || 0,
    // REF-6 — nets the Staff Tips ranking and carries the tip-out exposure icon.
    tipRefundsByCheckOwner: data?.tipRefundsByCheckOwner,
  });
};

/**
 * Money for the Tips & Gratuity drill-in only.
 *
 * EOD's shared `fmt` sets no `maximumFractionDigits`, so a computed average rendered as
 * "$33.488". Every other surface shows money at exactly 2dp; this matches them without
 * touching `fmt`, which the rest of the EOD screen depends on.
 */
const fmtMoney2 = (n: any) =>
  (n < 0 ? "-" : "") + getCurrencySymbol() +
  Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTgMoney = fmtMoney2;

// SW7 — the shared Sales Waterfall always shows 2 decimals on Snapshot/Sales (fmtCurrency);
// EOD's own `fmt` above rounds to whole dollars for its KPI cards/tables by design, so the
// waterfall uses the shared 2dp formatter rather than changing `fmt`'s 25 other call sites.
const fmtWaterfall = fmtMoney2;

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDateMMDDYYYY = (value: any) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
};
const fmtDateDisplay = (value: any) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${MONTHS_SHORT[Number(iso[2]) - 1]} ${Number(iso[3])}, ${iso[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};
const fmtCsvDateCell = (value: any) => `="${fmtDateMMDDYYYY(value)}"`;

const EMDASH = '\u2014';
/**
 * Strips corrupted multi-byte characters from API text.
 * â€ (\u00e2\u20ac) is the Windows-1252 mis-read of the first two UTF-8
 * bytes (E2 80) of any 3-byte Unicode character — strip that prefix plus
 * whatever byte follows.  Returns null for blank/dash-only strings so
 * callers can substitute a clean fallback.
 */
const sanitizeApiText = (val: any): string | null => {
  if (val == null) return null;
  const cleaned = String(val)
    .replace(/\u00e2\u20ac./g, '')   // â€ + any trailing char → remove
    .replace(/\u00e2\u0080./g, '')   // â\x80 + any trailing char → remove
    .trim();
  if (!cleaned || cleaned === EMDASH || cleaned === '\u2013') return null;
  return cleaned;
};

/* ── Tax Discrepancy interfaces ── */
interface PaymentDetail {
  orderId: string;
  tenderType: string;
  last4digit: string | null;
  cardType: string | null;
}
interface OrderItem {
  orderDate: string;
  orderNo: string;
  customerName: string;
  itemTotal: number;
  collectedTax: number;
  computedTax: number;
  difference: number;
  employeeName: string;
  paymentDetails: PaymentDetail[];
  splitCount: number;
}
interface TaxDiscrepancyResponse {
  content: OrderItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

function downloadCsv(filename: any, headers: any, rows: any, title?: string) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  // Native saves .xlsx so the currency symbol renders in Google Sheets Android
  // too (browser blob downloads do nothing inside the WebView).
  if (isNativePlatform()) {
    void exportTableXlsxNative(headers, rows, filename, title);
    return;
  }
  const dataRows = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(","));
  const body = title
    ? [escapeCsvCell(title), '', ...dataRows].join("\r\n")   // title row, blank row, then data
    : dataRows.join("\r\n");
  // BOM so Excel decodes the preserved currency symbol (₹) as UTF-8.
  const csv = `${CSV_BOM}${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function exportPDF(title: any, headers: any, rows: any) {
  // Capacitor native: render via jsPDF + share sheet (same as Snapshot/Sales).
  if (isNativePlatform()) {
    const fname = `${String(title).replace(/\s+/g, '_').toLowerCase()}.pdf`;
    const subtitle = `Exported on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
    void exportTablePdfNative(title, subtitle, headers, rows, fname);
    return;
  }
  const w = window.open('', '_blank');
  if (!w) return;
  const thStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#475569;background:#F5F6FA;text-transform:uppercase"';
  const tdStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;font-size:12px;color:#1A1D2E"';
  const ths = headers.map((h: any) => `<th ${thStyle}>${h}</th>`).join('');
  const trs = rows.map((r: any) => `<tr>${r.map((c: any) => `<td ${tdStyle}>${c}</td>`).join('')}</tr>`).join('');
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>body{font-family:'DM Sans',sans-serif;padding:40px;color:#1A1D2E}h1{font-size:18px;font-weight:700;margin-bottom:4px}.sub{font-size:12px;color:#475569;margin-bottom:20px}table{width:100%;border-collapse:collapse;margin-top:8px}.brand{display:flex;align-items:center;gap:10px;margin-bottom:24px}.logo{width:28px;height:28px;border-radius:8px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px}.bname{font-size:14px;font-weight:700}.bsub{font-size:9px;color:#64748B;letter-spacing:0.5px}@media print{body{padding:20px}button{display:none!important}}</style></head><body>
    <div class="brand"><div class="logo">M</div><div><div class="bname">Maghil</div><div class="bsub">ANALYTICS</div></div></div>
    <h1>${title}</h1>
    <div class="sub">Exported on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
    <button onclick="window.print()" style="padding:8px 20px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:12px;font-weight:600;cursor:pointer;margin-bottom:16px">Print / Save as PDF</button>
    <table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>
  </body></html>`);
  w.document.close();
}

function mobileExportPDF(title: any, headers: any, rows: any) {
  // Capacitor native: render via jsPDF + share sheet (same as Snapshot/Sales).
  if (isNativePlatform()) {
    const fname = `${String(title).replace(/\s+/g, '_').toLowerCase()}.pdf`;
    const subtitle = `Exported on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
    void exportTablePdfNative(title, subtitle, headers, rows, fname);
    return;
  }
  const thS = 'style="border:1px solid #E2E5EC;padding:6px 8px;text-align:left;font-size:10px;font-weight:600;color:#475569;background:#F5F6FA"';
  const tdS = 'style="border:1px solid #E2E5EC;padding:6px 8px;font-size:11px;color:#1A1D2E"';
  const ths = headers.map((h: any) => `<th ${thS}>${h}</th>`).join('');
  const trs = rows.map((r: any) => `<tr>${r.map((c: any) => `<td ${tdS}>${c}</td>`).join('')}</tr>`).join('');
  const html = `<!DOCTYPE html><html><head><title>${title}</title><link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"><style>body{font-family:'DM Sans',sans-serif;padding:20px;color:#1A1D2E}h1{font-size:16px;font-weight:700;margin-bottom:4px}.sub{font-size:11px;color:#475569;margin-bottom:16px}table{width:100%;border-collapse:collapse}@media print{button{display:none!important}}</style></head><body><div style="display:flex;align-items:center;gap:8px;margin-bottom:16px"><div style="width:24px;height:24px;border-radius:6px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px">M</div><div><span style="font-size:13px;font-weight:700">Maghil</span> <span style="font-size:8px;color:#64748B;letter-spacing:0.5px">ANALYTICS</span></div></div><h1>${title}</h1><div class="sub">Exported ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div><button onclick="window.print()" style="padding:8px 16px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:11px;font-weight:600;cursor:pointer;margin-bottom:16px">Print / Save PDF</button><table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (w) {
    w.onload = () => URL.revokeObjectURL(url);
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function ExportMenu({ onCSV, onPDF }: any) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="eod-download-btn"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.t3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
          <div className="eod-export-dropdown">
            <div className="eod-export-dropdown__item" onClick={() => { onCSV(); setOpen(false); }} onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              Export as CSV
            </div>
            <div className="eod-export-dropdown__divider" />
            <div className="eod-export-dropdown__item" onClick={() => { onPDF(); setOpen(false); }} onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              Export as PDF
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MobileExportBtn({ onCSV, onPDF }: any) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="eod-mobile-export-btn" aria-label="Export">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.t3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </button>
      {open && (
        <div className="eod-mobile-export-overlay" onClick={() => setOpen(false)}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} />
          <div className="eod-mobile-export-sheet" onClick={e => e.stopPropagation()}>
            <div className="eod-mobile-export-sheet__handle-row">
              <div className="eod-mobile-export-sheet__handle" />
            </div>
            <div className="eod-mobile-export-sheet__title">Export</div>
            <div className="eod-mobile-export-sheet__item" onClick={() => { onCSV(); setOpen(false); }}>
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--csv">
                <Icon name="table_chart" size={18} color={T.green} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as CSV</div>
                <div className="eod-mobile-export-sheet__item-desc">Spreadsheet-ready format</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__divider" />
            <div className="eod-mobile-export-sheet__item" onClick={() => { if (onPDF) onPDF(); setOpen(false); }}>
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--pdf">
                <Icon name="picture_as_pdf" size={18} color={T.accent} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as PDF</div>
                <div className="eod-mobile-export-sheet__item-desc">Print-ready document</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__cancel-row">
              <button onClick={() => setOpen(false)} className="eod-mobile-export-sheet__cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Summary Export: fetch all on Export click → shimmer → dropdown ─── */
function SummaryExportMenu({ fetchAll, onCSV, onPDF }: any) {
  const [state, setState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [cachedData, setCachedData] = useState<any>(null);

  const handleExportClick = () => {
    if (state === 'ready') { setState('idle'); setCachedData(null); return; }
    if (state === 'loading') return;
    setState('loading');
    fetchAll()
      .then((res: any) => { setCachedData(res); setState('ready'); })
      .catch(() => setState('idle'));
  };

  const close = () => { setState('idle'); setCachedData(null); };

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={handleExportClick} className="eod-download-btn" disabled={state === 'loading'}>
        {state === 'loading' ? (
          <span className="eod-export-shimmer-bar" />
        ) : (
          <>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.t3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </>
        )}
      </button>
      {state === 'ready' && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={close} />
          <div className="eod-export-dropdown">
            <div className="eod-export-dropdown__item" onClick={() => { onCSV(cachedData); close(); }} onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              Export as CSV
            </div>
            <div className="eod-export-dropdown__divider" />
            <div className="eod-export-dropdown__item" onClick={() => { onPDF(cachedData); close(); }} onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              Export as PDF
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryMobileExportBtn({ fetchAll, onCSV, onPDF }: any) {
  const [state, setState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [cachedData, setCachedData] = useState<any>(null);

  const handleClick = (e: any) => {
    e.stopPropagation();
    if (state === 'loading') return;
    setState('loading');
    fetchAll()
      .then((res: any) => { setCachedData(res); setState('ready'); })
      .catch(() => setState('idle'));
  };

  const close = () => { setState('idle'); setCachedData(null); };

  return (
    <div>
      <button onClick={handleClick} className="eod-mobile-export-btn" aria-label="Export" disabled={state === 'loading'}>
        {state === 'loading' ? (
          <span className="eod-export-shimmer-bar eod-export-shimmer-bar--sm" />
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.t3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        )}
      </button>
      {state === 'ready' && (
        <div className="eod-mobile-export-overlay" onClick={close}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} />
          <div className="eod-mobile-export-sheet" onClick={e => e.stopPropagation()}>
            <div className="eod-mobile-export-sheet__handle-row">
              <div className="eod-mobile-export-sheet__handle" />
            </div>
            <div className="eod-mobile-export-sheet__title">Export</div>
            <div className="eod-mobile-export-sheet__item" onClick={() => { onCSV(cachedData); close(); }}>
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--csv">
                <Icon name="table_chart" size={18} color={T.green} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as CSV</div>
                <div className="eod-mobile-export-sheet__item-desc">Spreadsheet-ready format</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__divider" />
            <div className="eod-mobile-export-sheet__item" onClick={() => { onPDF(cachedData); close(); }}>
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--pdf">
                <Icon name="picture_as_pdf" size={18} color={T.accent} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as PDF</div>
                <div className="eod-mobile-export-sheet__item-desc">Print-ready document</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__cancel-row">
              <button onClick={close} className="eod-mobile-export-sheet__cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Icon({ name, size = 20, color = T.t3, style = {} }: any) {
  return (
    <span
      className="material-symbols-rounded eod-icon"
      style={{ fontSize: size, color, ...style }}
    >
      {name}
    </span>
  );
}

function ReportCard({ title, subtitle, icon, action, children, flex = 1, minHeight, drillable, onTap, style = {} }: any) {
  const isDesktop = useIsDesktop();
  return (
    <div
      onClick={drillable ? onTap : undefined}
      className={`rpt-card rpt-tile eod-report-card ${!isDesktop ? "rpt-tile--compact" : ""} ${drillable ? "rpt-tile--drillable eod-report-card--drillable" : ""}`}
      style={{
        flex: flex || undefined,
        minHeight: minHeight ?? (isDesktop ? 220 : undefined),
        ...style,
      }}
    >
      {title != null && (
        <div className="eod-report-card__header">
          <div>
            <div className="rpt-section-title eod-report-card__title-group">
              {icon && <Icon name={icon} size={isDesktop ? 18 : 14} color={T.accent} />}
              {title}
            </div>
            {subtitle && <div className="rpt-subtitle" style={{ marginTop: 2, fontSize: isDesktop ? 11 : 10 }}>{subtitle}</div>}
          </div>
          {action ? (
            <div onClick={(e) => e.stopPropagation()}>{action}</div>
          ) : drillable ? (
            <svg width="10" height="10" viewBox="0 0 10 10" className="eod-report-card__chevron">
              <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : null}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * KPI delta percentages, always at 2dp.
 *
 * Deltas reach this card from two different places: most are `parseFloat` of a percentage the
 * API already reports, but the Tips & Grat delta is combined on the FE by `combineTgDeltaPct`
 * (ZAR-1762 / TG-1) and arrives as a raw float — it was rendering as
 * `-56.635847530239566%`. Formatting here rather than at either source keeps every card on this
 * screen consistent, and matches how Snapshot and Sales format their stamp-KPI deltas.
 */
const fmtDeltaPct = (d: any): string => {
  const n = Number(d);
  return Number.isFinite(n) ? n.toFixed(2) : String(d);
};

// ER 17 — `loading` follows the MiniK/MStatCard pattern (Snapshot/Sales): the value slot
// shimmers via Bone and sub/delta are hidden while loading, rather than a call site passing
// `value={loading ? '...' : real}`. Drill-down KPI rows (Refunds/Tender/Channels) pass this
// so a global-filter change while inside a drill shimmers just these cards, not the whole page.
function KPICard({ label, value, delta, deltaLabel, icon, iconColor, blurColor, cardBg, drillable, onTap, sub, tooltip, loading: isLoading }: any) {
  const isDesktop = useIsDesktop();
  return (
    <div
      onClick={drillable ? onTap : undefined}
      className={`rpt-tile eod-kpi ${!isDesktop ? "rpt-tile--compact" : ""} ${drillable ? "rpt-tile--drillable eod-kpi--drillable" : ""}`}
      style={{
        background: isDesktop && cardBg ? cardBg : undefined,
        borderColor: drillable ? "rgba(210,58,59,0.2)" : undefined,
      }}
    >
      {isDesktop && (blurColor || iconColor) && (
        <div className="eod-kpi__blur" style={{ background: blurColor || T.greenSoft }} />
      )}
      {drillable && (
        <svg width="10" height="10" viewBox="0 0 10 10" className="eod-kpi__chevron">
          <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {isDesktop ? (
        <>
          <div className="eod-kpi__desktop-header">
            <span className="rpt-tile__label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{label}{tooltip && <KpiTooltip text={tooltip} />}</span>
            {icon && <Icon name={icon} size={18} color={iconColor || T.t3} />}
          </div>
          {isLoading ? <Bone w={90} h={28} mb={6} /> : <div className="rpt-tile__value eod-kpi__desktop-value">{value}</div>}
          {!isLoading && sub && (
            <div className="rpt-tile__subtext eod-kpi__desktop-sub">{sub}</div>
          )}
          {!isLoading && delta !== undefined && (
            <div title={deltaLabel} className={`rpt-body eod-kpi__desktop-delta ${delta >= 0 ? "eod-kpi__desktop-delta--up" : "eod-kpi__desktop-delta--down"}`}>
              {delta >= 0 ? "↑" : "↓"} {delta >= 0 ? "+" : ""}{fmtDeltaPct(delta)}%
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rpt-card-label-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{label}{tooltip && <KpiTooltip text={tooltip} isMobile />}</div>
          {isLoading ? <Bone w={70} h={20} mb={4} /> : <div className="rpt-tile__value--sm eod-kpi__mobile-value">{value}</div>}
          {!isLoading && sub && (
            <div className="rpt-subtitle eod-kpi__mobile-sub">{sub}</div>
          )}
          {!isLoading && delta !== undefined && (
            <div title={deltaLabel} className="rpt-meta eod-kpi__mobile-delta" style={{ color: delta >= 0 ? T.green : T.red }}>
              {delta >= 0 ? "↑" : "↓"} {delta >= 0 ? "+" : ""}{fmtDeltaPct(delta)}%
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RevenueTrendChart({ data }: any) {
  const isDesktop = useIsDesktop();
  const id = useId().replace(/:/g, "");
  const height = isDesktop ? 220 : 120;
  const margin = isDesktop ? { top: 5, right: 10, left: 0, bottom: 0 } : { top: 5, right: 5, left: -15, bottom: 0 };
  const tickFont = isDesktop ? 10 : 8;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={margin}>
        <defs>
          <linearGradient id={`eodGross-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity={isDesktop ? 0.2 : 0.15} />
            <stop offset="100%" stopColor={T.accent} stopOpacity={0.01} />
          </linearGradient>
          <linearGradient id={`eodNet-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.green} stopOpacity={0.15} />
            <stop offset="100%" stopColor={T.green} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        {isDesktop && <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />}
        <XAxis dataKey="date" tick={{ fontSize: tickFont, fill: T.t3 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: tickFont === 10 ? 10 : 7, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`} />
        <Tooltip formatter={(v) => [fmt(v), ""]} labelStyle={{ color: T.t1 }} />
        <Area type="monotone" dataKey="gross" stroke={T.accent} strokeWidth={isDesktop ? 2.5 : 2} fill={`url(#eodGross-${id})`} name="Gross" />
        <Area type="monotone" dataKey="net" stroke={T.green} strokeWidth={isDesktop ? 2 : 1.5} strokeDasharray={isDesktop ? "4 2" : "3 2"} fill={isDesktop ? `url(#eodNet-${id})` : "transparent"} name="Net" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function normalizeTenderPercentages(items: any[]): any[] {
  if (!items.length) return items;
  const total = items.reduce((s: number, d: any) => s + (Number(d.value) || 0), 0);
  if (total === 0) return items;
  // Scale so values sum to exactly 100, using largest remainder method at 2dp
  const SCALE = 100; // 2 decimal places
  const TARGET = 10000; // 100.00 * 100
  const withFloor = items.map((d, i) => {
    const exact = (Number(d.value) / total) * 100 * SCALE;
    return { ...d, _idx: i, _floor: Math.floor(exact), _rem: exact - Math.floor(exact) };
  });
  const allocated = withFloor.reduce((s, d) => s + d._floor, 0);
  const remainder = TARGET - allocated;
  const sorted = [...withFloor].sort((a, b) => b._rem - a._rem);
  for (let i = 0; i < remainder; i++) sorted[i]._floor += 1;
  return sorted
    .sort((a, b) => a._idx - b._idx)
    .map(({ _idx, _floor, _rem, ...rest }) => ({ ...rest, value: _floor / SCALE }));
}

/** Locate a bearer/JWT token inside an arbitrary auth object (mirrors useEodSummaryData's findToken). */
function findAuthToken(obj: any, depth = 0): string | null {
  if (!obj || typeof obj !== 'object' || depth > 3) return null;
  for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
    if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
  }
  for (const [, val] of Object.entries(obj)) {
    if (typeof val === 'string' && (val as string).length > 50) return val as string;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const found = findAuthToken(val, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

const TENDER_EXPORT_COLORS = ['#D23A3B', '#4A90D9', '#D97706', '#16A34A', '#8B5CF6', '#9CA3B8'];

/** Same shape-building as useEodSummaryData's private buildTenderData — needed here to fetch an OTHER outlet's tender data for "All outlets" export. Reads the ClickHouse payments shape (`tenderType` / `tenderAmount` / `transactionCount`). */
function buildTenderDataForExport(paymentList: any[]): any[] {
  const toNum = (v: any): number => (v != null && v !== '' ? Number(v) : 0);
  const list = Array.isArray(paymentList) ? paymentList : [];
  const total = list.reduce((sum, row) => sum + toNum(row.tenderAmount), 0);
  if (total <= 0) return [];
  const byName: Record<string, any> = {};
  list.forEach((row: any, i: number) => {
    const name = String(row.tenderType ?? `Tender ${i + 1}`).trim() || 'Other';
    if (!byName[name]) byName[name] = { name, amount: 0, orders: 0, color: TENDER_EXPORT_COLORS[Object.keys(byName).length % TENDER_EXPORT_COLORS.length] };
    byName[name].amount += toNum(row.tenderAmount);
    byName[name].orders += toNum(row.transactionCount);
  });
  return Object.values(byName)
    .map((d: any) => ({
      name: d.name,
      value: total > 0 ? (d.amount / total) * 100 : 0,
      color: d.color,
      totalSales: d.amount,
      orders: d.orders,
    }))
    .filter((d: any) => d.value > 0)
    .sort((a: any, b: any) => b.value - a.value)
    .slice(0, 12);
}

/**
 * Fetch + normalize the tender breakdown for a SPECIFIC outlet (used by the Tender Type Breakdown
 * export's "All outlets" scope, which needs each OTHER outlet's own data — not the current outlet's
 * already-loaded `tenderList`). Mirrors useEodSummaryData's tender pipeline: the ClickHouse
 * `getLivePaymentsApi` with dates omitted on Today and sent for a historical range, carrying the
 * page's `orderSource` so an exported outlet is scoped to the same channel the screen is showing,
 * then the same buildTenderData + normalizeTenderPercentages transform.
 */
async function fetchTenderListForOutlet(
  outletLocationId: string,
  token: string | null,
  dateRange: { startDate: string; endDate: string },
  isToday: boolean,
  orderSource?: OrderSource
): Promise<any[]> {
  const res = await getLivePaymentsApi({
    locationId: outletLocationId,
    ...(isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
    ...(orderSource ? { orderSource } : {}),
    token,
  }).catch(() => null);
  const unwrapped = res?.metaDataInfo?.data ?? res?.content ?? res?.data ?? res?.result ?? res?.response ?? res;
  const paymentList = Array.isArray(unwrapped) ? unwrapped : [];
  const tenderData = buildTenderDataForExport(paymentList);
  return normalizeTenderPercentages(tenderData);
}

function TenderMixChart({ data }: any) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 140 : 100;
  const innerR = isDesktop ? 42 : 30;
  const outerR = isDesktop ? 58 : 44;
  const hasData = Array.isArray(data) && data.some((d: any) => Number(d?.value) > 0);
  if (!hasData) return <SectionEmpty kpiName="tender mix" minHeight={isDesktop ? 200 : 140} />;
  return (
    <div className="eod-tender-mix">
      <ResponsiveContainer width={size} height={size}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={innerR} outerRadius={outerR} dataKey="value" strokeWidth={0}>
            {data.map((d: any, i: number) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{ flex: 1 }}>
        {data.map((d: any) => (
          <div key={d.name} className="eod-tender-mix__legend-item">
            <div className="eod-tender-mix__legend-dot" style={{ background: d.color }} />
            <span className="eod-tender-mix__legend-name">{d.name}</span>
            <span className="eod-tender-mix__legend-value">{Number(d.value).toFixed(2)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ER 15 — the two drilldown copies of this chart (desktop `DesktopDrillContent` and mobile
// `MChannelsDrill`) always divided by 1000, so any value under $1k (i.e. every EOD-scale
// number) rounded to "$0k" on every tick. The parent `ChannelRevenueChart` above already gets
// this right; shared here instead of copied a third time.
const channelYAxisTick = (v: number) => v >= 1000 ? `${getCurrencySymbol()}${(v / 1000).toFixed(1)}k` : `${getCurrencySymbol()}${v.toFixed(0)}`;

type ChannelSeries = 'maghil' | 'thirdParty';

// ERB 2 — the single "peak" value label shared by the three Channel Revenue bar charts (main
// page, desktop drill, mobile drill). recharts 3 drops every zero-height bar from the rect list
// it hands to LabelList, so the `index` a label receives counts only that series' NON-ZERO bars,
// not data rows; the original `index === peakIdx` guard therefore labelled the wrong bar
// whenever a $0 bar sat before the peak, and no bar at all when the peak's row index exceeded
// the non-zero count. `valueAccessor` still sees the source row as `payload`, so the label value
// is resolved there by matching the row itself; every other bar gets `null` and draws nothing.
//
// Grouped charts label the single tallest bar with its own value, in that series' colour.
// Stacked charts label the tallest STACK with its total, sat on whichever segment is on top
// (thirdParty renders last, so it is the top unless it is $0 — then maghil is), in neutral ink
// because the number is the channel's total rather than either series' share.
function buildChannelPeakLabel(data: any[], opts: { stacked: boolean; fontSize: number }) {
  const { stacked, fontSize } = opts;
  const fmtPeak = (v: number) => v >= 1000 ? `${getCurrencySymbol()}${(v / 1000).toFixed(1)}k` : `${getCurrencySymbol()}${v.toFixed(0)}`;
  let peakRow: any;
  let peakSeries = '' as ChannelSeries | '';
  let peakValue = 0;
  let scan = 0;
  (Array.isArray(data) ? data : []).forEach((d: any) => {
    const m = Number(d?.maghil ?? 0);
    const tp = Number(d?.thirdParty ?? 0);
    if (stacked) {
      const total = m + tp;
      if (total > scan) { scan = total; peakRow = d; peakValue = total; peakSeries = tp > 0 ? 'thirdParty' : 'maghil'; }
    } else {
      if (m > scan) { scan = m; peakRow = d; peakValue = m; peakSeries = 'maghil'; }
      if (tp > scan) { scan = tp; peakRow = d; peakValue = tp; peakSeries = 'thirdParty'; }
    }
  });
  const color = stacked ? T.t1 : (peakSeries === 'maghil' ? T.accent : T.orange);
  const valueAccessor = (series: ChannelSeries) => (entry: any) => {
    if (series !== peakSeries || !peakRow) return null;
    const row = entry?.payload;
    const isPeakRow = row === peakRow || (peakRow?.channel != null && row?.channel === peakRow.channel);
    return isPeakRow ? peakValue : null;
  };
  const content = (props: any) => {
    const { x, y, width, value } = props;
    if (!Number(value)) return null;
    const cx = Number(x) + Number(width) / 2;
    return (
      <g>
        <text x={cx} y={Number(y) - 4} textAnchor="middle" fill={color} fontSize={fontSize} fontWeight={700}>{fmtPeak(Number(value))}</text>
      </g>
    );
  };
  return { valueAccessor, content };
}

function ChannelRevenueChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length || !label) return null;
  const row = payload[0]?.payload;
  const maghil = Number(row?.maghil ?? 0);
  const thirdParty = Number(row?.thirdParty ?? 0);
  const total = maghil + thirdParty;
  return (
    <div className="eod-channel-tooltip">
      <div className="eod-channel-tooltip__title">{label}</div>
      <div className="eod-channel-tooltip__total">Total: {fmt(total)}</div>
      <div className="eod-channel-tooltip__maghil">Brisque: {fmt(maghil)}</div>
      <div className="eod-channel-tooltip__third-party">Third Party: {fmt(thirdParty)}</div>
    </div>
  );
}

function ChannelRevenueChart({ data }: any) {
  const isDesktop = useIsDesktop();
  const height = isDesktop ? 240 : 150;
  const margin = isDesktop ? { top: 5, right: 10, left: 0, bottom: 28 } : { top: 5, right: 0, left: -20, bottom: 24 };
  const tickFont = isDesktop ? 10 : 7;
  const maxBar = isDesktop ? 24 : 16;
  const radius: any = isDesktop ? [4, 4, 0, 0] : [3, 3, 0, 0];
  const hasData = Array.isArray(data) && data.some((d: any) => Number(d?.maghil ?? 0) > 0 || Number(d?.thirdParty ?? 0) > 0);
  if (!hasData) return <SectionEmpty kpiName="channel revenue" minHeight={height} />;
  const peak = buildChannelPeakLabel(data, { stacked: false, fontSize: isDesktop ? 10 : 8 });
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ ...margin, top: (margin.top ?? 0) + 14 }}>
        <XAxis
          dataKey="channel"
          interval={0}
          tick={{ fontSize: tickFont, fill: T.t3 }}
          tickMargin={8}
          angle={-35}
          textAnchor="end"
          height={44}
          axisLine={false}
          tickLine={false}
        />
        <YAxis tick={{ fontSize: tickFont, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${getCurrencySymbol()}${(v / 1000).toFixed(1)}k` : `${getCurrencySymbol()}${v.toFixed(0)}`} />
        {/* ER 15 — cursor disabled outright (no hover fill at all): recharts' default is a
            semi-opaque black rect, which is the reported bug; explicitly requested over the
            grey-fill convention LoyaltyReport/VoiceAgentReport use elsewhere. */}
        <Tooltip content={<ChannelRevenueChartTooltip />} cursor={false} />
        <Bar dataKey="maghil" name="Brisque" fill={T.accent} radius={radius} maxBarSize={maxBar}>
          <LabelList valueAccessor={peak.valueAccessor('maghil')} content={peak.content} />
        </Bar>
        <Bar dataKey="thirdParty" name={isDesktop ? "Third Party" : "3rd Party"} fill={T.orange} radius={radius} maxBarSize={maxBar}>
          <LabelList valueAccessor={peak.valueAccessor('thirdParty')} content={peak.content} />
        </Bar>
        <Legend
          verticalAlign="bottom"
          align="center"
          iconType="square"
          iconSize={10}
          wrapperStyle={{ fontSize: isDesktop ? 12 : 10, color: T.t2 }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ── Payment helpers ── */
function getPaymentModeIconByTenderType(tenderType: string, cardType: string | null): string {
  const key = (cardType ?? tenderType ?? '').toUpperCase().replace(/[_\s-]/g, '');
  if (['VISA', 'VISACARD'].includes(key)) return 'credit_card';
  if (['MASTERCARD', 'MASTER', 'MC'].includes(key)) return 'credit_card';
  if (['APPLEPAY'].includes(key)) return 'phone_iphone';
  if (['GPAY', 'GOOGLEPAY'].includes(key)) return 'payment';
  if (['CASH'].includes(key)) return 'payments';
  if (['AMEX', 'AMERICANEXPRESS', 'AMERICAN'].includes(key)) return 'credit_card';
  if (['CHASE', 'DCVR', 'DISCOVER', 'CAPITALONE'].includes(key)) return 'credit_card';
  if (['DOORDASH', 'UBEREATS', 'GRUBHUB'].includes(key)) return 'delivery_dining';
  if (['CARDSWIPE', 'ONLINEKEY-IN'].includes(key)) return 'contactless';
  if (['OTHERS'].includes(key)) return 'more_horiz';
  return 'credit_card';
}

function formatPayment(p: PaymentDetail): { label: string; icon: string } {
  // UI: show cardType + last4digit when present, else fall back to tenderType
  const cardInfo = [p.cardType, p.last4digit].filter(Boolean).join(' ');
  const label = cardInfo || p.tenderType || '-';
  return { label, icon: getPaymentModeIconByTenderType(p.tenderType, p.cardType) };
}

function PaymentCell({ paymentDetails, splitCount }: { paymentDetails: any; splitCount: number }) {
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);

  if (!paymentDetails || (Array.isArray(paymentDetails) && paymentDetails.length === 0)) {
    return <span style={{ color: T.t3 }}>-</span>;
  }

  const payments: PaymentDetail[] = typeof paymentDetails === 'string'
    ? [{ tenderType: paymentDetails, last4digit: null, cardType: null, orderId: '' }]
    : paymentDetails;

  if (splitCount <= 1) {
    const { label, icon } = formatPayment(payments[0] ?? { tenderType: '-', last4digit: null, cardType: null, orderId: '' });
    return (
      <span title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', overflow: 'hidden' }}>
        <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t2, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 12, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </span>
    );
  }

  const openTooltip = () => {
    if (badgeRef.current) {
      const r = badgeRef.current.getBoundingClientRect();
      setTooltipPos({ top: r.top - 8, left: r.left + r.width / 2 });
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.blue, lineHeight: 1 }}>call_split</span>
      <span
        ref={badgeRef}
        className="eod-tax-disc__split-badge"
        onMouseEnter={openTooltip}
        onMouseLeave={() => setTooltipPos(null)}
        onClick={() => tooltipPos ? setTooltipPos(null) : openTooltip()}
      >
        +{splitCount}
      </span>
      {tooltipPos && (
        <div
          className="eod-tax-disc__split-tooltip"
          style={{ position: 'fixed', top: tooltipPos.top, left: tooltipPos.left, transform: 'translate(-50%, -100%)' }}
        >
          {payments.map((p, i) => {
            const { label, icon } = formatPayment(p);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                <span className="material-symbols-rounded" style={{ fontSize: 13, color: '#fff', lineHeight: 1 }}>{icon}</span>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}



const TAX_DISC_HEADERS = [
  { key: 'orderNo',       label: 'ORDER #',       sortKey: 'orderNo' },
  { key: 'orderDate',     label: 'DATE & TIME',   sortKey: 'orderDate' },
  { key: 'customerName',  label: 'CUSTOMER',      sortKey: 'customerName' },
  { key: 'itemTotal',     label: 'ORDER AMT',     sortKey: 'itemTotal' },
  { key: 'computedTax',   label: 'COMPUTED TAX',  sortKey: 'computedTax' },
  { key: 'collectedTax',  label: 'COLLECTED TAX', sortKey: 'collectedTax' },
  { key: 'paymentDetails',label: 'PAYMENT MODE',  sortKey: 'paymentDetails' },
  { key: 'employeeName',  label: 'STAFF',         sortKey: 'employeeName' },
];

// ER 15 — no longer takes its own `onBack`: the desktop in-content "Back to EOD Summary"
// button was removed in favor of the shared TopBar leftAddon (see EODDesktopContent), and
// mobile always relied on the TopBar's own back control, never on a prop here.
function TaxDiscrepancyDrillContent({ date }: { date: string }) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const exportDateRangeLabel = exportPeriodFor(date ? undefined : 'Today', date ? { startDate: date, endDate: date } : null)?.label ?? 'Today';
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const [apiData, setApiData] = useState<TaxDiscrepancyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortAsc, setSortAsc] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    getEodTaxDiscrepancy({ locationId, startDate: date, endDate: date, searchParam: '', page, size, sortBy, sortAsc, token })
      .then((res: any) => { if (id === fetchIdRef.current) setApiData(res); })
      .catch(() => { if (id === fetchIdRef.current) setApiData(null); })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  }, [locationId, date, page, size, sortBy, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [date, size]);

  const content = apiData?.content ?? [];
  const totalPages = apiData?.totalPages ?? 1;
  const totalElements = apiData?.totalElements ?? 0;

  const handleSort = (key: string) => {
    if (sortBy === key) setSortAsc(prev => !prev);
    else { setSortBy(key); setSortAsc(false); }
    setPage(1);
  };

  const fetchAllForExport = useCallback((overrideLocationId?: string): Promise<any> => {
    return getEodTaxDiscrepancy({
      locationId: overrideLocationId ?? locationId,
      startDate: date,
      endDate: date,
      searchParam: '',
      page: 1,
      size: totalElements || 10000,
      sortBy,
      sortAsc,
      token,
    });
  }, [locationId, date, totalElements, sortBy, sortAsc, token]);

  const fmtPaymentCsv = (paymentDetails: any): string => {
    if (!paymentDetails) return '-';
    const payments: any[] = typeof paymentDetails === 'string'
      ? [{ tenderType: paymentDetails, cardType: null, last4digit: null }]
      : paymentDetails;
    return payments.map((p: any) => {
      // Downloads format:
      //   tenderType + cardInfo both present → "GooglePay - VISA ...1111"
      //   only cardInfo (no tenderType)      → "VISA ...1111"
      //   only tenderType (no cardInfo)      → "GooglePay"
      const tenderType = sanitizeApiText(p.tenderType) ?? '';
      const cardInfo = [p.cardType, p.last4digit].filter(Boolean).join(' ');
      if (tenderType && cardInfo) return `${tenderType} - ${cardInfo}`;
      return tenderType || cardInfo || '-';
    }).join(', ') || '-';
  };

  const buildTaxDiscExportRows = (rows: OrderItem[]) => rows.map(row => [
    `#${row.orderNo}`,
    row.orderDate,
    sanitizeApiText(row.customerName) ?? '-',
    `${getCurrencySymbol()}${fmt2(row.itemTotal)}`,
    `${getCurrencySymbol()}${fmt2(row.computedTax)}`,
    `${getCurrencySymbol()}${fmt2(row.collectedTax)}`,
    fmtPaymentCsv(row.paymentDetails),
    sanitizeApiText(row.employeeName) ?? '-',
  ]);

  const taxDiscExportHeaders = TAX_DISC_HEADERS.map(h => h.label);

  const title = `Tax Discrepancy Audit Log (${fmtDateDisplay(date)})`;

  const exportAction = (
    <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
      title={title}
      filename={title.replace(/\s+/g, '_').replace(/[/\\?%*:|"<>]/g, '')}
      buildExport={async (_format, opts) => {
        const res = await fetchAllForExport(opts?.locationId);
        return { headers: taxDiscExportHeaders, rows: buildTaxDiscExportRows(res?.content ?? []) };
      }}
    />
  );

  const renderCell = (key: string, row: OrderItem) => {
    if (key === 'orderNo') return <span>#{row.orderNo}</span>;
    if (key === 'orderDate') return <span>{row.orderDate}</span>;
    if (key === 'customerName') return <span>{sanitizeApiText(row.customerName) ?? '-'}</span>;
    if (key === 'itemTotal') return <span>{getCurrencySymbol()}{fmt2(row.itemTotal)}</span>;
    if (key === 'computedTax') return <span>{getCurrencySymbol()}{fmt2(row.computedTax)}</span>;
    if (key === 'collectedTax') return <span>{getCurrencySymbol()}{fmt2(row.collectedTax)}</span>;
    if (key === 'paymentDetails') return <PaymentCell paymentDetails={row.paymentDetails} splitCount={row.splitCount} />;
    if (key === 'employeeName') return <span>{sanitizeApiText(row.employeeName) ?? '-'}</span>;
    return null;
  };

  if (isDesktop) {
    return (
      <div className="eod-tax-disc-drill">
        <ReportCard title={title} icon="gavel" flex={1} minHeight={0} action={exportAction}>
          <>
            {loading ? (
              <EodTableShimmer cols={8} rows={6} />
            ) : content.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: T.t3 }}>No discrepancies found for this date.</div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="eod-table eod-summary-table">
                    <thead>
                      <tr className="eod-table__row">
                        {TAX_DISC_HEADERS.map((h, i) => (
                          <th
                            key={h.key}
                            className={`eod-summary-table__th ${i === 0 ? 'eod-summary-table__th--date' : 'eod-summary-table__th--num'}`}
                            onClick={() => handleSort(h.sortKey)}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...(h.key === 'paymentDetails' && { maxWidth: 180, width: 180 }), ...(h.key === 'orderDate' && { textAlign: 'left' }) }}
                          >
                            <span className="eod-summary-table__th-inner">
                              {h.label}
                              {sortBy === h.sortKey && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {content.map((row, idx) => (
                        <tr key={row.orderNo + '-' + idx} className="eod-table__row">
                          {TAX_DISC_HEADERS.map((h, i) => (
                            <td
                              key={h.key}
                              className={`eod-summary-table__td ${i === 0 ? 'eod-summary-table__td--date' : 'eod-summary-table__td--num'}`}
                              style={h.key === 'paymentDetails' ? { maxWidth: 180 } : h.key === 'orderDate' ? { textAlign: 'left' } : undefined}
                            >
                              {renderCell(h.key, row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', padding: '10px 0 0', fontFamily: T.font }}>
                  <div className="eod-summary-footer__size">
                    <span>Rows per page:</span>
                    <select value={size} onChange={e => { setSize(Number(e.target.value)); setPage(1); }} className="eod-summary-footer__select">
                      {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <span style={{ textAlign: 'center', fontSize: 12, color: T.t3 }}>
                    Showing {Math.min((page - 1) * size + 1, totalElements)}–{Math.min(page * size, totalElements)} of {totalElements}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    {(() => {
                      const btnSt = (disabled: boolean): React.CSSProperties => ({
                        fontSize: 12, fontWeight: 500, fontFamily: T.font,
                        color: disabled ? T.t3 : T.t1,
                        background: T.surface, border: `1px solid ${T.cardBorder}`,
                        borderRadius: 6, padding: '6px 12px',
                        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
                      });
                      const pageBtnSt = (active: boolean): React.CSSProperties => ({
                        fontSize: 12, fontWeight: active ? 600 : 400, fontFamily: T.font,
                        color: active ? '#fff' : T.t1,
                        background: active ? T.accent : T.surface,
                        border: active ? `1px solid ${T.accent}` : `1px solid ${T.cardBorder}`,
                        borderRadius: 6, padding: '6px 10px', cursor: 'pointer', minWidth: 32, textAlign: 'center' as const,
                      });
                      const getPages = (): (number | '...')[] => {
                        const pages: (number | '...')[] = [];
                        if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
                        else {
                          pages.push(1);
                          if (page > 3) pages.push('...');
                          for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
                          if (page < totalPages - 2) pages.push('...');
                          pages.push(totalPages);
                        }
                        return pages;
                      };
                      return (
                        <>
                          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={btnSt(page <= 1)}>Prev</button>
                          {getPages().map((p, i) => p === '...'
                            ? <span key={`e${i}`} style={{ padding: '0 4px', color: T.t3 }}>...</span>
                            : <button key={p} onClick={() => setPage(p as number)} style={pageBtnSt(p === page)}>{p}</button>
                          )}
                          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnSt(page >= totalPages)}>Next</button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </>
        </ReportCard>
      </div>
    );
  }

  // Mobile
  const TAX_DISC_COL_WIDTHS: Record<string, number> = {
    orderNo: 80, orderDate: 120, customerName: 90, itemTotal: 80,
    computedTax: 90, collectedTax: 90, paymentDetails: 100, employeeName: 80,
  };

  return (
    <div className="eod-tax-disc-drill eod-tax-disc-drill--mobile">
      <ReportCard title={title} icon="gavel" flex={1} minHeight={0} action={exportAction}>
        <>
          {loading ? (
            <EodTableShimmer cols={8} rows={5} />
          ) : content.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: T.t3 }}>No discrepancies found for this date.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto', margin: '0 -14px' }}>
                <table className="eod-table eod-tax-disc-mobile-table" style={{ minWidth: 730, width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {TAX_DISC_HEADERS.map(h => (
                        <th
                          key={h.key}
                          onClick={() => handleSort(h.sortKey)}
                          style={{
                            width: TAX_DISC_COL_WIDTHS[h.key],
                            padding: '8px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: T.t3,
                            textAlign: h.key === 'orderNo' ? 'left' : 'right',
                            whiteSpace: 'nowrap',
                            borderBottom: `1px solid ${T.divider}`,
                            cursor: 'pointer',
                            letterSpacing: '0.4px',
                            background: T.surface,
                          }}
                        >
                          {h.label}
                          {sortBy === h.sortKey && <span className="eod-table__sort-icon" style={{ marginLeft: 3 }}>{sortAsc ? '↑' : '↓'}</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {content.map((row, idx) => (
                      <tr key={row.orderNo + '-' + idx} style={{ borderBottom: `1px solid ${T.divider}` }}>
                        {TAX_DISC_HEADERS.map(h => (
                          <td
                            key={h.key}
                            style={{
                              padding: '9px 10px',
                              fontSize: 12,
                              color: T.t1,
                              textAlign: h.key === 'orderNo' ? 'left' : 'right',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {renderCell(h.key, row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 2px 2px' }}>
                <span style={{ fontSize: 11, color: T.t3 }}>
                  Showing {Math.min((page - 1) * size + 1, totalElements)}–{Math.min(page * size, totalElements)} of {totalElements}
                </span>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className="eod-pagination__btn"
                    >Previous</button>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className="eod-pagination__btn"
                    >Next</button>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      </ReportCard>
    </div>
  );
}

// ZAR-1462: legacy WaterfallCard removed — replaced by the shared <SalesWaterfall />.

function ThirdPartyReconciliationCard({ data }: any) {
  const isDesktop = useIsDesktop();
  if (!Array.isArray(data) || data.length === 0) return <SectionEmpty kpiName="third-party reconciliation" minHeight={isDesktop ? 180 : 140} />;
  return (
    <>
      {data.map((p: any) => {
        const variance = p.gross - p.orderTotal;
        const isOk = Math.abs(variance) < 50;
        return (
          <div key={p.platform} className="eod-reconciliation__item">
            <div className="eod-reconciliation__header">
              <div className="eod-reconciliation__platform">
                <Icon name={p.icon} size={isDesktop ? 16 : 14} color={T.accent} />
                <span className="eod-reconciliation__platform-name">{p.platform}</span>
              </div>
              <span className={`eod-reconciliation__variance ${isOk ? "eod-reconciliation__variance--ok" : "eod-reconciliation__variance--warn"}`}>
                {variance >= 0 ? "+" : ""}{fmt(variance)}
              </span>
            </div>
            <div className="eod-reconciliation__details">
              <span>Gross: {fmt(p.gross)}</span>
              <span>Order Total: {fmt(p.orderTotal)}</span>
            </div>
          </div>
        );
      })}
    </>
  );
}

/* ─── Summary Report Table (backend-paginated, sortable, exportable) ─── */

/**
 * Token → date range for every EOD fetch.
 *
 * **Defers to the shared `resolveTokenRange`** (utils/reportPeriod, ZAR-1639) rather
 * than resolving tokens itself. This local copy predates the unified date picker and
 * only knew EOD's own presets — it had no `today` case at all, because
 * `DATE_PRESETS_EOD` omits the Today pill. Once the picker made the selected range
 * persist across reports, arriving on EOD from Sales with "Today" fell through to the
 * last-month fallback below: the control read "Today" while every EOD request asked
 * for the previous calendar month. The shared resolver also anchors on the store's
 * timezone and the 5 AM cutoff, which this copy never did.
 *
 * The fallback is kept only for a token the shared resolver cannot parse, which the
 * presets and the calendar should never produce.
 */
function parseDateRange(datePeriod: string, timeZoneCd?: string) {
  const shared = timeZoneCd ? resolveTokenRange(datePeriod, timeZoneCd) : resolveTokenRange(datePeriod);
  if (shared) return shared;

  const today = todayStr(timeZoneCd);
  const raw = typeof datePeriod === 'string' ? datePeriod.trim() : '';
  const normalized = raw.toLowerCase().replace(/\s+/g, '');
  if (raw.startsWith('custom:')) {
    const p = raw.split(':');
    if (p.length === 3) return { startDate: p[1], endDate: p[2] };
  }
  const d = new Date(`${today}T12:00:00`);
  if (normalized === 'yesterday') {
    const yd = new Date(d);
    yd.setDate(yd.getDate() - 1);
    const y = yd.getFullYear();
    const m = String(yd.getMonth() + 1).padStart(2, '0');
    const day = String(yd.getDate()).padStart(2, '0');
    const yStr = `${y}-${m}-${day}`;
    return { startDate: yStr, endDate: yStr };
  }
  if (normalized === 'thismonth') {
    const start = `${today.slice(0, 7)}-01`;
    return { startDate: start, endDate: today };
  }
  if (normalized === 'lastmonth') {
    const y = d.getFullYear(), m = d.getMonth(); // 0-indexed
    const prevMonth = m === 0 ? 11 : m - 1;
    const prevYear = m === 0 ? y - 1 : y;
    const lastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
    return { startDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-01`, endDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}` };
  }
  if (normalized === 'last3months' || normalized === 'last6months') {
    const n = normalized === 'last3months' ? 3 : 6;
    const y = d.getFullYear(), m = d.getMonth(); // 0-indexed current month
    // End = last day of the month immediately preceding the current month
    const endYear = m === 0 ? y - 1 : y;
    const endMonth1 = m === 0 ? 12 : m; // 1-indexed
    const endDay = new Date(endYear, endMonth1, 0).getDate();
    // Start = first day of the month that is N months before the end month
    const startIdx = m - n; // 0-indexed, may be negative
    const startYear = y + Math.floor(startIdx / 12);
    const startMonth1 = ((startIdx % 12) + 12) % 12 + 1; // 1-indexed
    return {
      startDate: `${startYear}-${String(startMonth1).padStart(2, '0')}-01`,
      endDate: `${endYear}-${String(endMonth1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`,
    };
  }
  if (normalized === 'thisyear') {
    return { startDate: `${d.getFullYear()}-01-01`, endDate: today };
  }
  if (normalized === 'lastyear') {
    const y = d.getFullYear() - 1;
    return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
  }
  // Fallback: last month
  const fm = d.getMonth() === 0 ? 11 : d.getMonth() - 1;
  const fy = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
  const fl = new Date(fy, fm + 1, 0).getDate();
  return { startDate: `${fy}-${String(fm + 1).padStart(2, '0')}-01`, endDate: `${fy}-${String(fm + 1).padStart(2, '0')}-${String(fl).padStart(2, '0')}` };
}

// Human-readable active date range for the "All outlets" export file header —
// preset names (e.g. "Today", "This Month") pass through as-is; "custom:start:end" formats nicely.
// The export header's date range comes from `exportPeriodFor` in utils/reportPeriod
// (DPFIX-07). The local copy that lived here returned a label only, never the year, and
// was one of seven that had already drifted apart — see DPFIX-05, where Snapshot's copy
// still tested the deleted 'Custom' token and leaked the raw one into a file.

/**
 * Summary Report columns. `key` is the mapped row field the cell renders (legacy
 * naming, kept so `fmtCell` / `buildExportRows` need no edit); `sortKey` is the
 * **API** sort field, i.e. the ClickHouse response field name that
 * `sales/live/v2/eodSummaryReport` sorts on. The two differ since the migration
 * — do not collapse them back together.
 */
const SUMMARY_DIRECT_HEADERS = [
  { key: 'date', label: 'DATE', sortKey: 'businessDate' },
  { key: 'maghilTotalSale', label: 'TOTAL SALES', sortKey: 'directSales' },
  { key: 'cashSale', label: 'CASH SALES', sortKey: 'cashSales' },
  { key: 'otherSale', label: 'ALTERNATIVE PAYMENTS', sortKey: 'otherSales' },
  { key: 'cardSale', label: 'CREDIT SALES (EXPECTED FUNDS)', sortKey: 'creditSales' },
];

const SUMMARY_THIRDPARTY_HEADERS = [
  { key: 'date', label: 'DATE', sortKey: 'businessDate' },
  { key: 'thirdPartyTotalSale', label: 'TOTAL SALES', sortKey: 'thirdPartySales' },
  { key: 'ubereats', label: 'UBER EATS', sortKey: 'uberEatsSales' },
  { key: 'grubhub', label: 'GRUBHUB', sortKey: 'grubhubSales' },
  { key: 'doordash', label: 'DOORDASH', sortKey: 'doordashSales' },
];

/**
 * Rows-per-page choices shared by all three EOD tables (Summary Report, Funds, Taxes).
 * Kept as EOD's own list rather than the shared pager's [10, 20, 30] default, because 5
 * has to stay selectable — it is the mobile default for all three, and an unlisted
 * current value would show on the trigger with nothing highlighted in the menu.
 */
const EOD_PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

/**
 * Map one ClickHouse `eodSummaryReport` row onto the column keys the table, the
 * cell formatter and the export builders already use. Keeping the legacy key
 * names means `SUMMARY_DIRECT_HEADERS` / `SUMMARY_THIRDPARTY_HEADERS`, the sort
 * keys, `fmtCell` and every export path stay untouched by the migration.
 *
 *   businessDate  → date                 directSales     → maghilTotalSale
 *   cashSales     → cashSale             otherSales      → otherSale
 *   creditSales   → cardSale             thirdPartySales → thirdPartyTotalSale
 *   uberEatsSales → ubereats             grubhubSales    → grubhub
 *   doordashSales → doordash
 *
 * The `totals` object carries `businessDate: null` — it renders as 'TOTALS',
 * exactly like the legacy endpoint's sentinel row did.
 */
function toSummaryTableRow(row: EodSummaryReportRow | null | undefined, isTotals = false): EodSummaryTableRow | null {
  if (!row) return null;
  const n = (v: any) => Number(v ?? 0);
  return {
    date: isTotals || !row.businessDate ? 'TOTALS' : String(row.businessDate).slice(0, 10),
    maghilTotalSale: n(row.directSales),
    cashSale: n(row.cashSales),
    otherSale: n(row.otherSales),
    cardSale: n(row.creditSales),
    thirdPartyTotalSale: n(row.thirdPartySales),
    ubereats: n(row.uberEatsSales),
    grubhub: n(row.grubhubSales),
    doordash: n(row.doordashSales),
    cashSaleRefund: n(row.cashSalesRefund),
    otherSaleRefund: n(row.otherSalesRefund),
    cardSaleRefund: n(row.creditSalesRefund),
  };
}

/** Unwrap + normalise a raw `eodSummaryReport` body into mapped rows + totals. */
function mapEodSummaryResponse(res: any): { content: EodSummaryTableRow[]; totals: EodSummaryTableRow | null } {
  const raw = res?.metaDataInfo?.data ?? res;
  const rows: EodSummaryReportRow[] = Array.isArray(raw?.rows) ? raw.rows : [];
  return {
    content: rows.map((r) => toSummaryTableRow(r)).filter(Boolean) as EodSummaryTableRow[],
    totals: toSummaryTableRow(raw?.totals ?? null, true),
  };
}

/**
 * Summary Report table — ClickHouse `sales/live/v2/eodSummaryReport`.
 *
 * Server-paginated and server-sorted, exactly like the legacy
 * `/eod/getEODReports` it replaces: `page` (ONE-based on the wire, zero-based in
 * state), `size`, `sortBy` (a ClickHouse response field name — see the header
 * constants) and `sortAsc`. Default page size follows the viewport, 10 on
 * desktop and 5 on mobile, because the mobile footer has no rows-per-page
 * selector. Everything the user sees — columns, the Direct / 3rd Party radio
 * toggle, the totals row, the pagination footer, the export button — is
 * unchanged from the legacy implementation.
 */
function SummaryReportTable({ datePeriod, orderSource, salesType, setSalesType }: {
  datePeriod: string;
  orderSource?: OrderSource;
  /** Lifted to EODSummaryReport — see the note at its declaration. */
  salesType: 'direct' | 'thirdParty';
  setSalesType: (v: 'direct' | 'thirdParty') => void;
}) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();
  const dispatch = useAppDispatch();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;
  // EODCH-TODAY — /sales/live/v2/eodSummaryReport is a ClickHouse "live" endpoint like the
  // rest of the family (useEodSummaryData already omits dates on Today for its calls); this
  // table's fetch never picked up that convention. Omitting startDate/endDate hits the live
  // window instead of a dated one.
  const isTodaySelected = useMemo(() => isTodayRange(dateRange.startDate, dateRange.endDate, todayStr()), [dateRange.startDate, dateRange.endDate]);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(isDesktop ? EOD_PAGE_SIZE_DESKTOP : EOD_PAGE_SIZE_MOBILE);
  const [sortBy, setSortBy] = useState(SUMMARY_DIRECT_HEADERS[0].sortKey);
  const [sortAsc, setSortAsc] = useState(EOD_SUMMARY_DEFAULT_SORT_ASC);

  // Follow the viewport default until the user picks a size, then leave it alone. The
  // shared pager offers the rows-per-page control on BOTH viewports, so a mobile pick is
  // a real, deliberate choice too and `sizeChosenRef` must respect it the same way.
  const sizeChosenRef = useRef(false);
  useEffect(() => {
    if (sizeChosenRef.current) return;
    setSize(isDesktop ? EOD_PAGE_SIZE_DESKTOP : EOD_PAGE_SIZE_MOBILE);
  }, [isDesktop]);

  const rawRows = useAppSelector((s) => s.eodClickhouse.eodSummaryReport);
  const rawTotals = useAppSelector((s) => s.eodClickhouse.eodSummaryReportTotals);
  const totalElements = useAppSelector((s) => s.eodClickhouse.eodSummaryReportTotalElements);
  const totalPages = useAppSelector((s) => s.eodClickhouse.eodSummaryReportTotalPages);
  const loading = useAppSelector((s) => s.eodClickhouse.eodSummaryReportLoading);
  const sectionError = useAppSelector((s) => s.eodClickhouse.eodSummaryReportFailure);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    dispatch(fetchEodSummaryReportRequest({
      locationId,
      ...(isTodaySelected ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
      page: page + 1, // state is zero-based, the wire is one-based
      size,
      sortBy,
      sortAsc,
      token,
    }));
  }, [dispatch, locationId, isTodaySelected, dateRange.startDate, dateRange.endDate, page, size, sortBy, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [dateRange.startDate, dateRange.endDate, size]);

  const rawContent = useMemo(
    () => (rawRows ?? []).map((r) => toSummaryTableRow(r)).filter(Boolean) as EodSummaryTableRow[],
    [rawRows],
  );
  const rawTotalsRow = useMemo(() => toSummaryTableRow(rawTotals, true), [rawTotals]);

  /*
   * REF-4 — Cash Sales, Alternative Payments and Credit Sales are shown exactly as the API
   * reports them (`cashSales` / `otherSales` / `creditSales`) — no FE netting against their
   * refund columns. A tender's refund total can exceed that same date's sales in that tender
   * (e.g. a cash refund against an order paid in cash on an earlier date), and subtracting
   * them here produced a negative, confusing "Cash Sales" figure. The refund amount is shown
   * as its own "−$X refunded" caption underneath instead (Direct only — the 3rd-party column
   * set has no tender to attribute a refund to).
   */
  const content = rawContent;
  const totals = rawTotalsRow;

  // REF-4 — "net of $X refunded" caption under a cell, straight off that row's own refund
  // field (`cashSaleRefund` / `otherSaleRefund` / `cardSaleRefund`, mapped by
  // `toSummaryTableRow` from the eodSummaryReport response).
  const TENDER_REFUND_COLUMN: Partial<Record<'cashSale' | 'otherSale' | 'cardSale', 'cashSaleRefund' | 'otherSaleRefund' | 'cardSaleRefund'>> = {
    cashSale: 'cashSaleRefund', otherSale: 'otherSaleRefund', cardSale: 'cardSaleRefund',
  };
  const refundedFor = (row: EodSummaryTableRow, key: string): number => {
    if (salesType !== 'direct') return 0;
    const refundCol = TENDER_REFUND_COLUMN[key as 'cashSale' | 'otherSale' | 'cardSale'];
    if (!refundCol) return 0;
    return Number((row as any)[refundCol] ?? 0);
  };

  /**
   * The single path for changing this table's column set — used by the radio below AND by
   * the global-filter sync beneath it, so the two can never leave the table in different
   * states.
   *
   * It also repairs `sortBy`: the two column sets share only `businessDate`, so a table
   * sorted by, say, `cashSales` and then switched to the 3rd-party columns would keep
   * sending a sort field that set does not contain. The functional `setSortBy` keeps this
   * callback free of a `sortBy` dependency, so the sync effect below fires on `orderSource`
   * alone rather than on every re-sort.
   */
  const applySalesType = useCallback((next: 'direct' | 'thirdParty') => {
    setSalesType(next);
    setPage(0);
    const nextHeaders = next === 'direct' ? SUMMARY_DIRECT_HEADERS : SUMMARY_THIRDPARTY_HEADERS;
    setSortBy((cur) => (nextHeaders.some((h) => h.sortKey === cur) ? cur : nextHeaders[0].sortKey));
  }, [setSalesType]);

  /**
   * Follow the global sales-channel filter: picking "Direct sales" selects this table's
   * Direct Sales radio, and "3rd party sales" selects 3rd Party Sales.
   *
   * "Overall sales" deliberately leaves the radio alone — the table has no Overall column
   * set to switch to (its two sets are Cash/Alternative/Credit vs Uber Eats/GrubHub/
   * DoorDash), so the last explicit choice stands. The radio also stays clickable: this is
   * a one-way sync from the global control, not a replacement for it.
   */
  useEffect(() => {
    if (orderSource === 'direct') applySalesType('direct');
    else if (orderSource === 'thirdparty') applySalesType('thirdParty');
  }, [orderSource, applySalesType]);

  const headers = salesType === 'direct' ? SUMMARY_DIRECT_HEADERS : SUMMARY_THIRDPARTY_HEADERS;

  const handleSort = (key: string) => {
    if (sortBy === key) setSortAsc(prev => !prev);
    else { setSortBy(key); setSortAsc(EOD_SUMMARY_DEFAULT_SORT_ASC); }
    setPage(0);
  };

  const fmtCell = (key: string, value: any) => {
    if (key === 'date') return value === 'TOTALS' ? 'TOTALS' : fmtDateDisplay(value);
    return getCurrencySymbol() + fmt2(Number(value ?? 0));
  };

  /* ── Export: refetch the whole range in one page (the store only holds the
        page on screen), optionally for another outlet. Same mapper either way. ── */
  const fetchAllForExport = useCallback(async (overrideLocationId?: string) => {
    const res = await getEodSummaryReportApi({
      locationId: overrideLocationId ?? locationId,
      ...(isTodaySelected ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
      page: EOD_SUMMARY_DEFAULT_PAGE,
      size: totalElements || 100,
      sortBy,
      sortAsc,
      token,
    });
    return mapEodSummaryResponse(res);
  }, [locationId, isTodaySelected, dateRange.startDate, dateRange.endDate, totalElements, sortBy, sortAsc, token]);

  const buildExportRows = (allContent: EodSummaryTableRow[], allTotals: EodSummaryTableRow | null, dateFn: (v: any) => string) =>
    [...allContent, ...(allTotals ? [allTotals] : [])].map((row: any) =>
      headers.map(h =>
        h.key === 'date'
          ? (row.date === 'TOTALS' ? 'TOTALS' : dateFn(row.date))
          : formatMoneyForExport(Number(row[h.key] ?? 0)),
      ),
    );

  const exportHeaders = headers.map(h => h.label);

  return (
    <ReportCard
      title="Summary Report"
      icon="summarize"
      flex={1}
      minHeight={0}
      action={
        <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
          title="Summary Report"
          filename="summary-report"
          buildExport={async (format, opts) => {
            const res = await fetchAllForExport(opts?.locationId);
            // REF-4 — exported exactly as the API reports it, same as the on-screen table —
            // no FE netting against the refund columns.
            const rows = format === 'pdf'
              ? buildExportRows(res.content, res.totals, fmtDateDisplay)
              : buildExportRows(res.content, res.totals, fmtCsvDateCell);
            return { headers: exportHeaders, rows, totalRow: !!res.totals };
          }}
        />
      }
    >
      <>
        {/* Radio toggle: Direct Sales / 3rd Party Sales */}
        <div className="eod-summary-radio-row">
          <label className={`eod-summary-radio ${salesType === 'direct' ? 'eod-summary-radio--active' : ''}`}>
            <input type="radio" name="summaryReportType" checked={salesType === 'direct'} onChange={() => applySalesType('direct')} />
            <span className="eod-summary-radio__dot" />
            Direct Sales
          </label>
          <label className={`eod-summary-radio ${salesType === 'thirdParty' ? 'eod-summary-radio--active' : ''}`}>
            <input type="radio" name="summaryReportType" checked={salesType === 'thirdParty'} onChange={() => applySalesType('thirdParty')} />
            <span className="eod-summary-radio__dot" />
            3rd Party Sales
          </label>
        </div>

        {loading ? (
          <EodTableShimmer cols={5} rows={6} />
        ) : sectionError ? (
          <SectionError kpiName="summary report" onRetry={fetchData} size="medium" minHeight={240} />
        ) : content.length === 0 ? (
          <SectionEmpty kpiName="summary report" size="medium" minHeight={220} />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="eod-table eod-summary-table">
                <thead>
                  <tr className="eod-table__row">
                    {headers.map((h, i) => (
                      <th
                        key={h.key}
                        className={`eod-summary-table__th ${i === 0 ? 'eod-summary-table__th--date' : 'eod-summary-table__th--num'}`}
                        onClick={h.sortKey ? () => handleSort(h.sortKey) : undefined}
                      >
                        <span className="eod-summary-table__th-inner">
                          {h.label}
                          {h.sortKey && sortBy === h.sortKey && (
                            <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {content.map((row: any, idx: number) => (
                    <tr key={row.date + '-' + idx} className="eod-table__row">
                      {headers.map((h, i) => {
                        const refunded = refundedFor(row, h.key);
                        return (
                          <td
                            key={h.key}
                            className={`eod-summary-table__td ${i === 0 ? 'eod-summary-table__td--date' : 'eod-summary-table__td--num'}`}
                          >
                            {fmtCell(h.key, row[h.key])}
                            {refunded > 0 && (
                              <span style={{ display: 'block', fontSize: 10.5, color: T.red, fontWeight: 600 }}>
                                −{fmtCell(h.key, refunded)} refunded
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {totals && (
                    <tr className="eod-table__row eod-table__row--totals">
                      {headers.map((h, i) => {
                        const refunded = refundedFor(totals as any, h.key);
                        return (
                          <td
                            key={h.key}
                            className={`eod-summary-table__td ${i === 0 ? 'eod-summary-table__td--date' : 'eod-summary-table__td--num'}`}
                          >
                            {fmtCell(h.key, (totals as any)[h.key])}
                            {refunded > 0 && (
                              <span style={{ display: 'block', fontSize: 10.5, color: T.red, fontWeight: 600 }}>
                                −{fmtCell(h.key, refunded)} refunded
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <ReportTablePager
              page={page} pageSize={size} total={totalElements} totalPages={totalPages}
              isDesktop={isDesktop} onPageChange={setPage}
              sizeOptions={EOD_PAGE_SIZE_OPTIONS}
              onPageSizeChange={(s) => { sizeChosenRef.current = true; setSize(s); setPage(0); }}
            />
          </>
        )}
      </>
    </ReportCard>
  );
}


/**
 * Map one ClickHouse `eodSummaryReport` row onto the Funds table's three
 * columns. Only three response keys are read:
 *
 *   businessDate → date        creditSales → expected        maghilFunding → processed
 *
 * The target field names are the legacy `/eod/getEODReports` ones, so the row
 * renderer, the expected-vs-processed mismatch highlight and the export builder
 * needed no edit. `totals` carries `businessDate: null` and renders as 'TOTALS',
 * exactly like the legacy sentinel row did.
 */
function toFundsTableRow(row: EodSummaryReportRow | null | undefined, isTotals = false): EodFundsTableRow | null {
  if (!row) return null;
  return {
    date: isTotals || !row.businessDate ? 'TOTALS' : String(row.businessDate).slice(0, 10),
    // Expected Funds = credit sales as collected, the same figure as the Summary table's Credit Sales column.
    expected: Number(row.creditSales ?? 0),
    processed: Number(row.maghilFunding ?? 0),
  };
}

/** Unwrap + normalise a raw `eodSummaryReport` body into mapped Funds rows + totals. */
function mapEodFundsResponse(res: any): { content: EodFundsTableRow[]; totals: EodFundsTableRow | null } {
  const raw = res?.metaDataInfo?.data ?? res;
  const rows: EodSummaryReportRow[] = Array.isArray(raw?.rows) ? raw.rows : [];
  return {
    content: rows.map((r) => toFundsTableRow(r)).filter(Boolean) as EodFundsTableRow[],
    totals: toFundsTableRow(raw?.totals ?? null, true),
  };
}

/**
 * Funds Table Section — ClickHouse `sales/live/v2/eodSummaryReport`.
 *
 * Shares the endpoint with the Summary Report table above but keeps its own
 * page / size / sort state, so it dispatches its own action into its own slice
 * of the store. Server-paginated and server-sorted like the legacy
 * `/eod/getEODReports` it replaces: `page` (ONE-based on the wire, zero-based in
 * state), `size`, `sortBy` — always `businessDate`, Date being the only sortable
 * header — and `sortAsc`. Columns, the expected-vs-processed mismatch highlight,
 * the totals row, the pagination footer and the export button are unchanged.
 */
function FundsTableSection({ datePeriod, orderSource }: { datePeriod: string; orderSource?: OrderSource }) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();
  const dispatch = useAppDispatch();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;
  // EODCH-TODAY — same fix as the Summary Report table above: this shares the same
  // ClickHouse /sales/live/v2/eodSummaryReport endpoint and needs the same live-window
  // omission on Today.
  const isTodaySelected = useMemo(() => isTodayRange(dateRange.startDate, dateRange.endDate, todayStr()), [dateRange.startDate, dateRange.endDate]);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(isDesktop ? EOD_PAGE_SIZE_DESKTOP : EOD_PAGE_SIZE_MOBILE);
  const [sortBy, setSortBy] = useState<string>(EOD_FUNDS_SORT_BY);
  const [sortAsc, setSortAsc] = useState(EOD_SUMMARY_DEFAULT_SORT_ASC);

  const handleSort = (key: string) => {
    if (sortBy === key) setSortAsc((p) => !p);
    else { setSortBy(key); setSortAsc(EOD_SUMMARY_DEFAULT_SORT_ASC); }
    setPage(0);
  };

  // Same rule as the Summary Report table above — follow the viewport default until the
  // user picks a size, on either viewport, then leave it alone. `4b476c26` deliberately kept
  // this table on a flat 10 to avoid shortening the mobile list, but 10 rows overflow the
  // phone card badly, so it now matches the table directly above it instead of disagreeing
  // with it.
  const sizeChosenRef = useRef(false);
  useEffect(() => {
    if (sizeChosenRef.current) return;
    setSize(isDesktop ? EOD_PAGE_SIZE_DESKTOP : EOD_PAGE_SIZE_MOBILE);
  }, [isDesktop]);

  const rawRows = useAppSelector((s) => s.eodClickhouse.eodFundsReport);
  const rawTotals = useAppSelector((s) => s.eodClickhouse.eodFundsReportTotals);
  const totalElements = useAppSelector((s) => s.eodClickhouse.eodFundsReportTotalElements);
  const totalPages = useAppSelector((s) => s.eodClickhouse.eodFundsReportTotalPages);
  const loading = useAppSelector((s) => s.eodClickhouse.eodFundsReportLoading);
  const sectionError = useAppSelector((s) => s.eodClickhouse.eodFundsReportFailure);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    dispatch(fetchEodFundsReportRequest({
      locationId,
      ...(isTodaySelected ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
      page: page + 1, // state is zero-based, the wire is one-based
      size,
      sortBy,
      sortAsc,
      token,
    }));
  }, [dispatch, locationId, isTodaySelected, dateRange.startDate, dateRange.endDate, page, size, sortBy, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [dateRange.startDate, dateRange.endDate, size]);

  const content = useMemo(
    () => (rawRows ?? []).map((r) => toFundsTableRow(r)).filter(Boolean) as EodFundsTableRow[],
    [rawRows],
  );
  const totals = useMemo(() => toFundsTableRow(rawTotals, true), [rawTotals]);

  /* ── Export: refetch the whole range in one page (the store only holds the
        page on screen), optionally for another outlet. Same mapper either way. ── */
  const fetchAllForExport = useCallback(async (overrideLocationId?: string) => {
    const res = await getEodSummaryReportApi({
      locationId: overrideLocationId ?? locationId,
      ...(isTodaySelected ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
      page: EOD_SUMMARY_DEFAULT_PAGE,
      size: totalElements || 100,
      sortBy,
      sortAsc,
      token,
    });
    return mapEodFundsResponse(res);
  }, [locationId, isTodaySelected, dateRange.startDate, dateRange.endDate, totalElements, sortBy, sortAsc, token]);

  const buildExportRows = (allContent: EodFundsTableRow[], allTotals: EodFundsTableRow | null, dateFn: (d: string) => string) =>
    [...allContent, ...(allTotals ? [allTotals] : [])].map((r) => [
      r.date === 'TOTALS' ? 'TOTALS' : dateFn(r.date ?? ''),
      formatMoneyForExport(Number(r.expected)),
      formatMoneyForExport(Number(r.processed)),
    ]);

  const renderRow = (row: EodFundsTableRow, key: string, isTotals = false) => {
    const dateLabel = isTotals ? 'TOTALS' : fmtDateDisplay(row.date);
    const pm = Math.abs(row.expected - row.processed) > 0.005;
    if (isDesktop) {
      return (
        <tr key={key} className={`eod-table__row${isTotals ? ' eod-table__row--totals' : ''}`}>
          <td className="eod-table__td--first">{dateLabel}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(row.expected)}</td>
          <td className={`eod-table__td--last ${pm ? 'eod-table__cell--highlight-orange' : 'eod-table__cell'}`}>
            {getCurrencySymbol()}{fmt2(row.processed)}
          </td>
        </tr>
      );
    }
    return (
      <div key={key} className={`eod-mobile-table__row${isTotals ? ' eod-mobile-table__row--totals' : ''}`}>
        <span className="eod-mobile-table__date eod-mobile-table__date--funds">{dateLabel}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(row.expected)}</span>
        <span className={`eod-mobile-table__cell eod-mobile-table__cell--last ${pm ? 'eod-table__cell--highlight-orange' : ''}`}>
          {getCurrencySymbol()}{fmt2(row.processed)}
        </span>
      </div>
    );
  };

  const tableBody = (
    <>
      {loading ? (
        <EodTableShimmer cols={3} rows={6} />
      ) : sectionError ? (
        <SectionError kpiName="funds report" onRetry={fetchData} size="medium" minHeight={240} />
      ) : content.length === 0 ? (
        <SectionEmpty kpiName="funds report" size="medium" minHeight={220} />
      ) : (
        <>
          {isDesktop ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="eod-table">
                <thead>
                  <tr className="eod-table__row">
                    <th className="eod-table__th eod-table__th--first" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(EOD_FUNDS_SORT_BY)}>
                      Date {sortBy === EOD_FUNDS_SORT_BY && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                    </th>
                    <th className="eod-table__th eod-table__th--mid" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('creditSales')}>
                      Expected Funds {sortBy === 'creditSales' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                    </th>
                    <th className="eod-table__th eod-table__th--last" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('maghilFunding')}>
                      Processed Funds {sortBy === 'maghilFunding' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {content.map((row, i) => renderRow(row, row.date + '-' + i))}
                  {totals && renderRow(totals, 'totals-row', true)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="eod-mobile-table">
              <div className="eod-mobile-table__inner">
                <div className="eod-mobile-table__header">
                  <span className="eod-mobile-table__date--funds" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(EOD_FUNDS_SORT_BY)}>
                    Date {sortBy === EOD_FUNDS_SORT_BY && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                  </span>
                  <span className="eod-mobile-table__cell" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('creditSales')}>
                    Expected Funds {sortBy === 'creditSales' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                  </span>
                  <span className="eod-mobile-table__cell eod-mobile-table__cell--last" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('maghilFunding')}>
                    Processed Funds {sortBy === 'maghilFunding' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                  </span>
                </div>
                {content.map((row, i) => renderRow(row, row.date + '-' + i))}
                {totals && renderRow(totals, 'totals-row', true)}
              </div>
            </div>
          )}
          <ReportTablePager
            page={page} pageSize={size} total={totalElements} totalPages={totalPages}
            isDesktop={isDesktop} onPageChange={setPage}
            sizeOptions={EOD_PAGE_SIZE_OPTIONS}
            onPageSizeChange={(s) => { sizeChosenRef.current = true; setSize(s); setPage(0); }}
          />
        </>
      )}
    </>
  );

  return (
    <ReportCard
      title="Funds"
      subtitle="Expected vs Processed"
      icon="account_balance"
      flex={1}
      minHeight={0}
      action={
        <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
          title="Funds Report"
          filename="funds-report"
          buildExport={async (format, opts) => {
            const res = await fetchAllForExport(opts?.locationId);
            const rows = format === 'pdf'
              ? buildExportRows(res.content, res.totals, fmtDateDisplay)
              : buildExportRows(res.content, res.totals, fmtCsvDateCell);
            return { headers: ['Date', 'Expected Funds', 'Processed Funds'], rows, totalRow: !!res.totals };
          }}
        />
      }
    >
      {tableBody}
    </ReportCard>
  );
}

/* ─── Taxes Table Section — self-fetching with backend pagination + totals ─── */
function TaxesTableSection({
  datePeriod, taxesRef, onTaxDrill, orderSource,
  // ER 8 — page/size/sizeChosen are lifted into EODSummaryReport (see the comment there)
  // so a discrepancy-row drill-in and "Back to EOD Summary" doesn't lose the page the user
  // was on: aliased back to their old local names so the body below is unchanged.
  taxesPage: page, setTaxesPage: setPage,
  taxesSize: size, setTaxesSize: setSize,
  taxesSizeChosen: sizeChosen, setTaxesSizeChosen: setSizeChosen,
}: any) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;
  // EODCH-TODAY — getEodTaxesData now calls /sales/live/v2/getTaxesData (moved off the
  // legacy /eod/getTaxesData path), so this follows the same "omit dates = live today"
  // contract as the rest of the /sales/live/v2/* family above, rather than being an
  // unverified assumption on a legacy REST endpoint.
  const isTodaySelected = useMemo(() => isTodayRange(dateRange.startDate, dateRange.endDate, todayStr()), [dateRange.startDate, dateRange.endDate]);

  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sectionError, setSectionError] = useState(false);
  const fetchIdRef = useRef(0);
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortAsc, setSortAsc] = useState(EOD_SUMMARY_DEFAULT_SORT_ASC);

  const handleSort = (key: string) => {
    if (sortBy === key) setSortAsc((p) => !p);
    else { setSortBy(key); setSortAsc(EOD_SUMMARY_DEFAULT_SORT_ASC); }
    setPage(0);
  };

  // Same rule as the Summary Report and Funds tables — follow the viewport default until
  // the user picks a size, on either viewport, then leave it alone. `size` is a dependency
  // of `fetchData` below, so the viewport default reaches the WIRE too: a phone requests
  // `size=5`, not ten rows it then has to squeeze into the card.
  useEffect(() => {
    if (sizeChosen) return;
    setSize(isDesktop ? EOD_PAGE_SIZE_DESKTOP : EOD_PAGE_SIZE_MOBILE);
  }, [isDesktop, sizeChosen, setSize]);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    setSectionError(false);
    getEodTaxesData({ locationId, ...(isTodaySelected ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }), page: page + 1, size, sortBy, sortAsc, token })
      .then((res: any) => { if (id !== fetchIdRef.current) return; setApiData(res); setSectionError(false); })
      .catch(() => { if (id !== fetchIdRef.current) return; setApiData(null); setSectionError(true); })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  }, [locationId, isTodaySelected, dateRange, page, size, sortBy, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // ER 8 — page/size are lifted (see EODSummaryReport), so this component gets a FRESH mount
  // every time the user returns from the Tax Discrepancy Audit Log ("Back to EOD Summary"
  // swaps TaxDiscrepancyDrillContent back out for this whole subtree). A reactive
  // `useEffect(() => setPage(0), [dateRange.startDate, dateRange.endDate, size])` used to
  // live here, but an effect ALWAYS runs on its first render too — so on that fresh mount it
  // fired again and stomped the just-restored page straight back to 0, even though
  // dateRange/size hadn't actually changed. There is no reliable way to tell "mount" from
  // "real change" from inside this effect, so the reset is handled at the SOURCE instead:
  // `handleDatePeriodChange` in EODSummaryReport resets `taxesPage` when the date range
  // itself changes, and `onPageSizeChange` below already resets it when the size changes —
  // both are real, one-shot user actions, not something a remount can accidentally replay.

  const content: any[] = apiData?.content ?? [];
  const totals: any = apiData?.totals ?? null;
  const totalPages: number = apiData?.totalPages ?? 1;
  const totalElements: number = apiData?.totalElements ?? 0;

  const fetchAllForExport = useCallback((overrideLocationId?: string): Promise<any> =>
    getEodTaxesData({ locationId: overrideLocationId ?? locationId, ...(isTodaySelected ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }), page: 1, size: totalElements || 100, sortBy, sortAsc, token }),
  [locationId, isTodaySelected, dateRange, totalElements, sortBy, sortAsc, token]);

  /*
   * REF-4 — "Tax refunds are never recalculated against taxable sales." Taxable Sales,
   * Computed Tax, Collected Tax and Difference stay exactly as the API returns them; the
   * refund is added as two NEW columns rather than folded into any existing one.
   *
   * `taxRefunded` and `netTaxRetained` now come straight off the same getTaxesData row
   * (real data, per-row on both a date row and the `date: 'TOTALS'` totals row) — read
   * directly rather than recomputed from the fixture-backed `useRefundPeriod`, so this
   * table can never disagree with the backend's own reconciliation math.
   */
  const taxRefundedFor = (row: any): number => Number(row.taxRefunded ?? 0);
  const netTaxRetainedFor = (row: any): number => Number(row.netTaxRetained ?? 0);

  const buildExportRows = (allContent: any[], allTotals: any, dateFn: (v: any) => string) =>
    [...allContent, ...(allTotals ? [allTotals] : [])].map((row: any) => [
      row.date === 'TOTALS' ? 'TOTALS' : dateFn(row.date ?? ''),
      formatMoneyForExport(Number(row.taxableSales ?? 0)),
      formatMoneyForExport(Number(row.computedTax ?? 0)),
      formatMoneyForExport(Number(row.collectedTax ?? 0)),
      formatMoneyForExport(taxRefundedFor(row)),
      formatMoneyForExport(Number(row.difference ?? 0)),
      formatMoneyForExport(netTaxRetainedFor(row)),
    ]);

  const renderRow = (row: any, key: string, isTotals = false) => {
    const hd = Number(row.difference ?? 0) > 0.05;
    const taxRefunded = taxRefundedFor(row);
    const netTaxRetained = netTaxRetainedFor(row);
    const cls = isTotals ? 'eod-table__row eod-table__row--totals' : 'eod-table__row';
    if (isDesktop) {
      return (
        <tr key={key} className={cls}>
          <td className="eod-table__td--first">{isTotals ? 'TOTALS' : fmtDateDisplay(row.date)}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(Number(row.taxableSales ?? 0))}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(Number(row.computedTax ?? 0))}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(Number(row.collectedTax ?? 0))}</td>
          <td className="eod-table__td--mid eod-table__cell">{taxRefunded > 0 ? `${getCurrencySymbol()}${fmt2(taxRefunded)}` : '—'}</td>
          <td className={`eod-table__td--mid eod-table__cell ${hd ? 'eod-table__cell--highlight-red' : 'eod-table__cell--highlight-green'}`}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {getCurrencySymbol()}{fmt2(Number(row.difference ?? 0))}
              {hd && !isTotals && onTaxDrill && (
                <span className="eod-tax-disc__drill-icon" title="View order details" onClick={() => onTaxDrill(row.date)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </span>
              )}
            </span>
          </td>
          <td className="eod-table__td--last eod-table__cell">{getCurrencySymbol()}{fmt2(netTaxRetained)}</td>
        </tr>
      );
    }
    return (
      <div key={key} className={`eod-mobile-table__row${isTotals ? ' eod-mobile-table__row--totals' : ''}`}>
        <span className="eod-mobile-table__date eod-mobile-table__date--taxes">{isTotals ? 'TOTALS' : fmtDateDisplay(row.date)}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(Number(row.taxableSales ?? 0))}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(Number(row.computedTax ?? 0))}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(Number(row.collectedTax ?? 0))}</span>
        <span className="eod-mobile-table__cell">{taxRefunded > 0 ? `${getCurrencySymbol()}${fmt2(taxRefunded)}` : '—'}</span>
        <span className={`eod-mobile-table__diff ${hd ? 'eod-table__cell--highlight-red' : 'eod-table__cell--highlight-green'}`}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {getCurrencySymbol()}{fmt2(Number(row.difference ?? 0))}
            {hd && !isTotals && onTaxDrill && (
              <span className="eod-tax-disc__drill-icon" title="View order details" onClick={() => onTaxDrill(row.date)}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </span>
            )}
          </span>
        </span>
        <span className="eod-mobile-table__cell eod-mobile-table__cell--last">{getCurrencySymbol()}{fmt2(netTaxRetained)}</span>
      </div>
    );
  };

  const tableBody = (
    <>
      {loading ? (
        <EodTableShimmer cols={7} rows={6} />
      ) : sectionError ? (
        <SectionError kpiName="taxes report" onRetry={fetchData} size="medium" minHeight={240} />
      ) : content.length === 0 ? (
        <SectionEmpty kpiName="taxes report" size="medium" minHeight={220} />
      ) : (
        <>
          {isDesktop ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="eod-table eod-table--taxes">
                <thead>
                  <tr className="eod-table__row">
                    <th className="eod-table__th eod-table__th--first" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('date')}>
                      Date {sortBy === 'date' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                    </th>
                    <th className="eod-table__th eod-table__th--mid" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('taxableSales')}>
                      Taxable Sales {sortBy === 'taxableSales' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                    </th>
                    <th className="eod-table__th eod-table__th--mid">Computed Tax</th>
                    <th className="eod-table__th eod-table__th--mid">Collected Tax</th>
                    <th className="eod-table__th eod-table__th--mid">Tax Refunded</th>
                    <th className="eod-table__th eod-table__th--mid">Difference</th>
                    <th className="eod-table__th eod-table__th--last">Net Tax Retained</th>
                  </tr>
                </thead>
                <tbody>
                  {content.map((row: any, i: number) => renderRow(row, row.date + '-' + i))}
                  {totals && renderRow(totals, 'totals-row', true)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="eod-mobile-table">
              <div className="eod-mobile-table__inner--wide">
                <div className="eod-mobile-table__header">
                  <span className="eod-mobile-table__date--taxes" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('date')}>
                    Date {sortBy === 'date' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                  </span>
                  <span className="eod-mobile-table__cell" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('taxableSales')}>
                    Taxable Sales {sortBy === 'taxableSales' && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                  </span>
                  <span className="eod-mobile-table__cell">Computed Tax</span>
                  <span className="eod-mobile-table__cell">Collected Tax</span>
                  <span className="eod-mobile-table__cell">Tax Refunded</span>
                  <span className="eod-mobile-table__diff">Difference</span>
                  <span className="eod-mobile-table__cell eod-mobile-table__cell--last">Net Tax Retained</span>
                </div>
                {content.map((row: any, i: number) => renderRow(row, row.date + '-' + i))}
                {totals && renderRow(totals, 'totals-row', true)}
              </div>
            </div>
          )}
          <ReportTablePager
            page={page} pageSize={size} total={totalElements} totalPages={totalPages}
            isDesktop={isDesktop} onPageChange={setPage}
            sizeOptions={EOD_PAGE_SIZE_OPTIONS}
            onPageSizeChange={(s) => { setSizeChosen(true); setSize(s); setPage(0); }}
          />
        </>
      )}
    </>
  );

  return (
    <div ref={taxesRef}>
      <ReportCard
        title="Taxes"
        subtitle={isDesktop ? 'Computed vs Collected tax reconciliation' : 'Computed vs Collected'}
        icon="receipt_long"
        flex={1}
        minHeight={0}
        action={
          <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
            title="Taxes Report"
            filename="taxes-report"
            buildExport={async (format, opts) => {
              const res = await fetchAllForExport(opts?.locationId);
              const rows = format === 'pdf'
                ? buildExportRows(res?.content ?? [], res?.totals ?? null, fmtDateDisplay)
                : buildExportRows(res?.content ?? [], res?.totals ?? null, fmtCsvDateCell);
              return { headers: ['Date', 'Taxable Sales', 'Computed Tax', 'Collected Tax', 'Tax Refunded', 'Difference', 'Net Tax Retained'], rows, totalRow: !!res?.totals };
            }}
          />
        }
      >
        {tableBody}
      </ReportCard>
    </div>
  );
}

/* ─── Sales-breakdown waterfall (ZAR-1462) ───
 * EODCH-WATERFALL-SOURCE — EOD gained the global sales-channel filter well after this
 * component was written, and the `source` prop passed to the shared `SalesWaterfall` was
 * never updated to follow it: it stayed hard-coded to "overall", so selecting "3rd party
 * sales" kept rendering the full two-zone ladder (Gross Sales → Voids → Discounts →
 * Complimentary → Refunds → Net Sales → Tax → Tips & Gratuity → Others → Total Collected)
 * instead of Snapshot's/Sales' honest 3rd-party view (Item Total + Tax = Total Paid, no
 * deduction rows, no Net line — `SalesWaterfall`'s `source === 'thirdparty'` branch). Now
 * resolved from `orderSource` exactly like Snapshot's/Sales' own `waterfallSource`.
 *
 * EODCH-API-AUDIT — it used to dispatch all five of its ClickHouse sources itself, but
 * three of them (snapshot-details, refund-summary, staff-perfomance) are the very calls
 * useEodSummaryData already makes for the hero KPIs and the Tips & Gratuity drill, with
 * identical params — so every EOD load fired them twice. Those three now arrive as
 * `waterfall` off the page's data. Only void-summary and comp-order-log are dispatched
 * here: nothing else on EOD reads them.
 *
 * Both of those follow the global sales-channel filter, as do the three the hook supplies
 * — so the whole ladder is scoped to one channel and its steps stay reconcilable. Voids and
 * Complimentary are still fetched under 3rd party (cheap, and Direct/Overall need them
 * regardless) even though the thirdparty ladder no longer renders them as rungs — matching
 * Snapshot's/Sales' own thirdparty view, which drops those rows entirely rather than
 * showing them at their (3rd-party-scoped) values. */
function EodSalesWaterfall({ datePeriod, waterfall, orderSource }: { datePeriod: string; waterfall?: any; orderSource?: OrderSource }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // SW3 — EOD's waterfall has no adjacent KPI row of its own (unlike Snapshot/Sales' Net Sales
  // drill) — its own KPI cards are page-level, further up, unrelated to the waterfall. So the
  // return destination here is the waterfall section itself, not a KPI row.
  const waterfallRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchParams.get('scrollTo') !== 'waterfall') return;
    requestAnimationFrame(() => waterfallRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { selectedBranch: branch } = useBranch();
  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);

  const liveVoidSummary = useAppSelector((s) => s.clickHouse.liveVoidSummarySuccess) as { cancelledAmt: number }[];
  const liveCompOrderLog = useAppSelector((s) => s.clickHouse.liveCompOrderLog) as { amount: number }[];
  const voidsLoading = useAppSelector((s) => s.clickHouse.liveVoidSummaryLoading);
  const compLoading = useAppSelector((s) => s.clickHouse.liveCompOrderLogLoading);
  const loading = voidsLoading || compLoading;

  useEffect(() => {
    if (!locationId) return;
    // ZAR-1762 — these ClickHouse live endpoints return today's rows ONLY when
    // startDate/endDate are omitted. This effect used to send them unconditionally, so on
    // Today it re-fired with dates and came back empty. Same resolved-range rule as
    // everywhere else: EOD has no Today pill, so the date picker is the only route to
    // today and must behave identically — and it is the same rule useEodSummaryData
    // applies, so the `waterfall` values below cover exactly the same window as these two.
    const { startDate, endDate } = dateRange;
    const isToday = isTodayRange(startDate, endDate, todayStr());
    const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate, endDate };
    dispatch(fetchLiveVoidSummaryRequest(locationId, chDates.startDate, chDates.endDate, orderSource));
    dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: 10, ...chDates, ...(orderSource ? { orderSource } : {}) }));
  }, [dispatch, locationId, dateRange.startDate, dateRange.endDate, orderSource]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = buildWaterfallData({
    voids: liveVoidSummary.reduce((s, i) => s + (Number(i.cancelledAmt) || 0), 0),
    complimentary: liveCompOrderLog.reduce((s, o) => s + (Number(o.amount) || 0), 0),
    discounts: Number(waterfall?.discounts) || 0,
    refunds: Number(waterfall?.refunds) || 0,
    netSales: Number(waterfall?.netSales) || 0,
    tax: Number(waterfall?.tax) || 0,
    tips: Number(waterfall?.tips) || 0,
    gratuity: Number(waterfall?.gratuity) || 0,
    otherCharges: Number(waterfall?.otherCharges) || 0,
    totalCollected: Number(waterfall?.totalCollected) || 0,
    // Service Charge — same snapshot-details-sourced figure as Snapshot/Sales.
    serviceCharge: waterfall?.serviceCharge == null ? null : Number(waterfall.serviceCharge) || 0,
    serviceChargeRetained: !!waterfall?.serviceChargeRetained,
    // REF-1 — real item/fee refund split off snapshot-details, replacing the
    // fixture-backed refundSummary for the waterfall's Refunds/Others rows.
    refundContext: waterfallRefundContextFromSnapshot(waterfall?.itemRefund, waterfall?.feeRefund),
  });

  // EODCH-WATERFALL-SOURCE — same resolution Snapshot/Sales use for their own
  // `waterfallSource`; `orderSource` is `'direct' | 'thirdparty' | undefined` (undefined =
  // Overall sales), matching WaterfallSource once undefined maps to 'overall'.
  const waterfallSource: WaterfallSource = orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall';

  // SW3 — EOD has no Voids/Discounts/Complimentary/Refunds drill content of its own for the
  // waterfall (its "Total Refunds" stamp KPI drill is a separate entry point); all four route
  // to Snapshot's respective drilldown instead, consistent across desktop and mobile (this
  // component renders both — one unified responsive screen, no device fork). Under 3rd party
  // this is moot anyway — the thirdparty ladder has no deduction rows to click.
  return (
    <div ref={waterfallRef}>
      <SalesWaterfall
        source={waterfallSource}
        data={data}
        fmt={fmtWaterfall}
        loading={loading}
        // SW3 — EOD's waterfall lives on the main page (not behind its own drillView), so
        // returning just needs to land back on the plain EOD route — the ?scrollTo=waterfall
        // param (read above) brings the waterfall itself into view on arrival.
        onDeductionClick={(key) => {
          // REF-2 — the waterfall's own "Refunds ›" row opens the ONE shared Refunds
          // drill-in too, same destination as the "Total Refunds" KPI.
          if (key === 'refunds') {
            navigate(refundsPath({
              origin: 'eod',
              dateFilter: datePeriod,
              startDate: dateRange?.startDate,
              endDate: dateRange?.endDate,
              source: orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall',
            }));
            return;
          }
          navigate(`/reports/snapshot?drill=${key}`, { state: { returnTo: '/reports/eod?scrollTo=waterfall' } });
        }}
      />
    </div>
  );
}

/* ─── Shared responsive content (desktop + mobile) ─── */
function EODContent({
  onDrill, onTaxDrill, taxesRef, data, datePeriod, orderSource, salesType, setSalesType,
  taxesPage, setTaxesPage, taxesSize, setTaxesSize, taxesSizeChosen, setTaxesSizeChosen,
}: any) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const eodLocationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const sbcDateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const sbcIsToday = useMemo(
    () => String(datePeriod || '').trim().toLowerCase().replace(/\s+/g, '') === 'today',
    [datePeriod],
  );

  // Same resolution the other EOD section exports use, so this KPI's download
  // states the period in the same words as theirs.
  const sbcExportDateRangeLabel = exportPeriodFor(datePeriod, sbcDateRange)?.label ?? datePeriod;

  // Sales by Channel — Financial Breakdown (ZAR-1555)
  // Follows the global channel filter: it is a ClickHouse call and does accept
  // `orderSource`, so under Direct or 3rd party it narrows to that channel's groups.
  const salesByChannelBreakdown = useSalesByChannelBreakdown({
    locationId: eodLocationId,
    startDate: sbcDateRange.startDate,
    endDate: sbcDateRange.endDate,
    isToday: sbcIsToday,
    orderSource,
  });

  const kpiItems = data?.kpiItems?.length ? data.kpiItems : buildKpiItemsDefault(orderSource === 'thirdparty');
  const tenderDataActual = normalizeTenderPercentages(data?.tenderData ?? []);
  const channelDataActual = data?.channelData ?? [];

  return (
    <div className="eod-content">
      {/* KPI row */}
      <div className="eod-content__kpi-row">
        {kpiItems.map((item: any) => (
          <KPICard
            key={item.label}
            label={item.label}
            value={item.value}
            delta={item.delta}
            deltaLabel={item.deltaLabel}
            icon={item.icon}
            iconColor={item.iconColor}
            blurColor={item.blurColor}
            cardBg={item.cardBg}
            sub={item.sub}
            tooltip={item.tooltip}
            drillable={!!onDrill && !!item.drillKey}
            onTap={onDrill && item.drillKey ? () => onDrill(item.drillKey) : undefined}
          />
        ))}
      </div>

      <div className="eod-content__chart-row">
        <ReportCard
          title="Tender Mix"
          subtitle={isDesktop ? "Revenue by payment method" : undefined}
          icon="pie_chart"
          flex={0.7}
          minHeight={isDesktop ? 220 : undefined}
          drillable={!!onDrill}
          onTap={onDrill ? () => onDrill("tender") : undefined}
        >
          <TenderMixChart data={tenderDataActual} />
        </ReportCard>
        <ReportCard
          title="Channel Revenue"
          subtitle="Brisque vs Third-Party by channel"
          icon="device_hub"
          flex={1.3}
          minHeight={isDesktop ? 200 : undefined}
          drillable={!!onDrill}
          onTap={onDrill ? () => onDrill("channels") : undefined}
        >
          <ChannelRevenueChart data={channelDataActual} />
        </ReportCard>
      </div>

      {datePeriod && <SummaryReportTable datePeriod={datePeriod} orderSource={orderSource} salesType={salesType} setSalesType={setSalesType} />}

      {/* Hidden under "3rd party sales": Expected Funds (creditSales) and Processed Funds
          (maghilFunding) are both first-party settlement figures — a marketplace's payout to
          the outlet is handled off-platform and isn't represented by either field, so this
          table has nothing meaningful to show for a 3rd-party-only view. Unmounting it also
          stops its ClickHouse eodSummaryReport call, same saved-request reasoning as Taxes
          below. */}
      {datePeriod && orderSource !== 'thirdparty' && <FundsTableSection datePeriod={datePeriod} orderSource={orderSource} />}

      {/* Hidden under "3rd party sales": tax is collected and remitted on the outlet's own
          sales, so a marketplace-only view of the Taxes table has nothing meaningful to
          reconcile. Unmounting it also stops its /sales/live/v2/getTaxesData call firing, so the 3rd
          party view costs one request fewer. The section is a full-width block, so the
          column closes the gap on its own — no reflow handling needed. */}
      {orderSource !== 'thirdparty' && (
        <TaxesTableSection
          datePeriod={datePeriod} taxesRef={taxesRef} onTaxDrill={onTaxDrill} orderSource={orderSource}
          taxesPage={taxesPage} setTaxesPage={setTaxesPage}
          taxesSize={taxesSize} setTaxesSize={setTaxesSize}
          taxesSizeChosen={taxesSizeChosen} setTaxesSizeChosen={setTaxesSizeChosen}
        />
      )}

      {/* Sales by Channel — Financial Breakdown (ZAR-1555, before the Sales waterfall) */}
      <SalesByChannelBreakdown
        data={salesByChannelBreakdown.groups}
        totals={salesByChannelBreakdown.totals}
        loading={salesByChannelBreakdown.loading}
        error={salesByChannelBreakdown.error}
        onRetry={salesByChannelBreakdown.retry}
        outletName={(branch as any)?.name}
        dateRange={{ startDate: sbcDateRange.startDate, endDate: sbcDateRange.endDate, isToday: sbcIsToday, orderSource }}
        dateRangeLabel={sbcExportDateRangeLabel}
      />

      {/* ZAR-1462: existing "Sales to Funding" waterfall replaced by the shared
          sales-breakdown waterfall (EOD is always the blended "overall" source). */}
      <div className="eod-content__chart-row">
        {datePeriod && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <EodSalesWaterfall datePeriod={datePeriod} waterfall={data?.waterfall} orderSource={orderSource} />
          </div>
        )}
      </div>
    </div>
  );
}

const DRILL_TITLES_DESKTOP: any = { tender: "Tender Types", tips: TG_DRILL_TITLE, refunds: "Refunds", channels: "Channels", taxDiscrepancy: "Tax Discrepancy Audit Log" };

function DesktopDrillContent({ view, data, loading, onBack, dateRange, isToday, orderSource }: any) {
  const exportDateRangeLabel = exportPeriodFor(isToday ? 'Today' : undefined, isToday ? null : dateRange)?.label ?? 'Today';
  const chData = data?.channelData ?? [];
  const drillPeak = buildChannelPeakLabel(chData, { stacked: false, fontSize: 10 });
  const tenderList = normalizeTenderPercentages(data?.tenderData ?? []);
  const cs = data?.channelSummary;
  const { auth } = useAuth();
  const authToken = useMemo(() => (auth ? findAuthToken(auth) : null), [auth]);

  if (view === "tender") {
    const hasBreakdown = tenderList.length && tenderList.some((r: any) => r.totalSales != null || r.orders != null);
    const totalCollected = hasBreakdown ? tenderList.reduce((s: number, r: any) => s + (Number(r.totalSales) || 0), 0) : 0;
    const topTenders = hasBreakdown ? tenderList.slice(0, 3) : [];
    const buildTenderExportRows = (rows: any[]) => rows.map((r: any) => {
      const orders = Number(r.orders) || 0;
      const sales = Number(r.totalSales) || 0;
      const avgTicket = orders > 0 ? sales / orders : 0;
      return [
        r.name,
        orders > 0 ? orders : '—',
        sales > 0 ? formatMoneyForExport(sales) : '—',
        avgTicket > 0 ? formatMoneyForExport(avgTicket) : '—',
        `${Number(r.value).toFixed(2)}%`,
      ];
    });
    return (
      <div className="eod-drill">
        <div className="eod-drill__kpi-row">
          <KPICard label="Total Collected" value={totalCollected > 0 ? fmt(totalCollected) : "—"} loading={loading} />
          {topTenders.map((t: any) => (
            <KPICard key={t.name} label={t.name} value={fmt(t.totalSales ?? 0)} loading={loading} />
          ))}
        </div>
        <ReportCard
          title="Tender Type Breakdown"
          subtitle="Detailed sales by payment method"
          icon="list_alt"
          action={hasBreakdown ? (
            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
              variant="desktop"
              title="Tender Type Breakdown"
              filename="eod-tender-type-breakdown"
              buildExport={async (_format, opts) => {
                const rows = opts?.locationId
                  ? await fetchTenderListForOutlet(opts.locationId, authToken, dateRange, !!isToday, orderSource)
                  : tenderList;
                return {
                  headers: ['Tender type', 'Transactions', 'Total sales', 'Avg ticket', '% of total'],
                  rows: buildTenderExportRows(rows),
                };
              }}
            />
          ) : undefined}
        >
          {loading ? (
            <EodTableShimmer cols={5} rows={6} />
          ) : hasBreakdown ? (
            <div style={{ overflowX: "auto" }}>
              <table className="eod-tender-table">
                <thead>
                  <tr className="eod-tender-table__thead-row">
                    <th className="eod-tender-table__th eod-tender-table__th--first">Tender type</th>
                    <th className="eod-tender-table__th" style={{ textAlign: "right" }}>Transactions</th>
                    <th className="eod-tender-table__th" style={{ textAlign: "right" }}>Total sales</th>
                    <th className="eod-tender-table__th" style={{ textAlign: "right" }}>Avg ticket</th>
                    <th className="eod-tender-table__th eod-tender-table__th--last" style={{ textAlign: "right" }}>% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {tenderList.map((r: any) => {
                    const orders = Number(r.orders) || 0;
                    const sales = Number(r.totalSales) || 0;
                    const avgTicket = orders > 0 ? sales / orders : 0;
                    return (
                      <tr key={r.name} className="eod-tender-table__row">
                        <td className="eod-tender-table__name">{r.name}</td>
                        <td className="eod-tender-table__cell">{orders > 0 ? orders.toLocaleString() : "—"}</td>
                        <td className="eod-tender-table__cell eod-tender-table__cell--bold">{sales > 0 ? fmt(sales) : "—"}</td>
                        <td className="eod-tender-table__cell eod-tender-table__cell--muted">{avgTicket > 0 ? getCurrencySymbol() + avgTicket.toFixed(2) : "—"}</td>
                        <td className="eod-tender-table__cell eod-tender-table__cell--accent eod-tender-table__cell--last">{Number(r.value).toFixed(2)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : tenderList.length > 0 ? (
            tenderList.map((r: any, i: number, arr: any[]) => (
              <div key={r.name} className={`eod-tender-list__item ${i < arr.length - 1 ? "eod-tender-list__item--bordered" : ""}`}>
                <Icon name="credit_card" size={16} color={T.accent} style={{ marginRight: 8 }} />
                <div style={{ flex: 1 }}>
                  <div className="eod-tender-list__name">{r.name}</div>
                </div>
                <div className="eod-tender-list__value">
                  <div className="eod-tender-list__pct">{r.value != null ? `${Number(r.value).toFixed(2)}%` : "—"}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="eod-no-data">No tender data for this period.</div>
          )}
        </ReportCard>
      </div>
    );
  }
  // ZAR-1762 / TG-2 - "Tips & Gratuity" drill-in, direct-only (EOD has no source toggle
  // and no 3rd-party gratuity context). Section totals come from the EOD summary, which is
  // the reconciliation-grade figure; the staff rankings come from staffTipAndGratuity.
  if (view === "tips") {
    return (
      <div className="eod-drill">
        <TipsGratuityDrill data={buildEodTipsGratuityData(data)} fmt={fmtTgMoney} />
      </div>
    );
  }
  if (view === "refunds") {
    const rs = data?.refundsSummary;
    const reasons = data?.refundReasons ?? [];
    const refundVal = rs?.refundAmount != null ? fmt(rs.refundAmount) : "—";
    const refundRateVal = rs?.refundRate != null ? `${rs.refundRate.toFixed(1)}%` : "—";
    return (
      <div className="eod-drill">
        <div className="eod-drill__kpi-row">
          <KPICard label="Refunds" value={refundVal} loading={loading} />
          <KPICard label="Refund Rate" value={refundRateVal} loading={loading} />
        </div>
        {/* ER 15 — `minHeight` explicitly zeroed: `ReportCard` defaults desktop cards to
            220px, sized for a fixed-height chart/table, but this list is 1..N reasons tall.
            With a single reason that left ~170px of dead space below it. */}
        <ReportCard title="Refund Reasons" icon="help_outline" minHeight={0} style={{ marginBottom: 16 }}>
          {loading ? (
            <div>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <Bone w={100} h={11} r={4} />
                    <Bone w={60} h={11} r={4} />
                  </div>
                  <Bone w={null} h={5} r={3} />
                </div>
              ))}
            </div>
          ) : reasons.length ? reasons.map((r: any) => (
            <div key={r.reason} className="eod-refund-reason">
              <div className="eod-refund-reason__header">
                <span className="eod-refund-reason__name">{r.reason}</span>
                <span className="eod-refund-reason__value">
                  {r.amount != null ? `${fmt(Number(r.amount) || 0)} (${r.pct}%)` : `${r.pct}%`}
                </span>
              </div>
              <div className="eod-refund-reason__bar">
                <div className="eod-refund-reason__bar-fill" style={{ width: `${r.pct}%`, background: r.color || T.accent }} />
              </div>
            </div>
          )) : (
            <SectionEmpty kpiName="refund reasons" />
          )}
        </ReportCard>
      </div>
    );
  }
  if (view === "channels") {
    // EODCH-CHANNEL — all three money cards at 2dp (the shared `fmt` rounds to whole units by
    // design for the rest of the screen). Every figure follows the global sales-channel filter,
    // which the by-channel call now carries (EODCH-CHANNEL-2): under Direct the Third Party
    // card reads 0 and Maghil % reads 100%, and under 3rd party the mirror image.
    const totalSalesVal = cs?.totalSales != null ? fmtMoney2(cs.totalSales) : (cs?.totalMaghil != null || cs?.totalThirdParty != null ? fmtMoney2((cs?.totalMaghil ?? 0) + (cs?.totalThirdParty ?? 0)) : "—");
    const maghilVal = cs?.totalMaghil != null ? fmtMoney2(cs.totalMaghil) : "—";
    const thirdPartyVal = cs?.totalThirdParty != null ? fmtMoney2(cs.totalThirdParty) : "—";
    const maghilPctVal = cs?.maghilPct != null ? `${cs.maghilPct.toFixed(1)}%` : "—";
    return (
      <div className="eod-drill">
        <div className="eod-drill__kpi-row">
          <KPICard label="Total Sales" value={totalSalesVal} loading={loading} />
          <KPICard label="Brisque" value={maghilVal} loading={loading} />
          <KPICard label="Third Party" value={thirdPartyVal} loading={loading} />
          <KPICard label="Brisque %" value={maghilPctVal} loading={loading} />
        </div>
        <ReportCard title="Revenue by Channel" icon="bar_chart" style={{ maxWidth: 560 }}>
          {loading ? (
            <Bone h={260} r={8} />
          ) : chData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chData} margin={{ top: 19, right: 0, left: -20, bottom: 28 }}>
                <XAxis
                  dataKey="channel"
                  interval={0}
                  tick={{ fontSize: 10, fill: T.t3 }}
                  tickMargin={8}
                  angle={-35}
                  textAnchor="end"
                  height={44}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={channelYAxisTick} />
                <Tooltip content={<ChannelRevenueChartTooltip />} cursor={false} />
                <Bar dataKey="maghil" name="Brisque" fill={T.accent} radius={[4, 4, 0, 0]} maxBarSize={28}>
                  <LabelList valueAccessor={drillPeak.valueAccessor('maghil')} content={drillPeak.content} />
                </Bar>
                <Bar dataKey="thirdParty" name="Third Party" fill={T.orange} radius={[4, 4, 0, 0]} maxBarSize={28}>
                  <LabelList valueAccessor={drillPeak.valueAccessor('thirdParty')} content={drillPeak.content} />
                </Bar>
                <Legend verticalAlign="bottom" align="center" iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12, color: T.t2 }} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="eod-no-data">No channel data for this period.</div>
          )}
        </ReportCard>
      </div>
    );
  }
  return null;
}

function EODDesktopContent({
  datePeriod, setDatePeriod, channel, setChannel, orderSource, salesType, setSalesType,
  data, loading, error, locationId, dateRange, isToday,
  taxesPage, setTaxesPage, taxesSize, setTaxesSize, taxesSizeChosen, setTaxesSizeChosen,
}: any) {
  const [drillView, setDrillView] = useState<any>(null);
  const [taxDrillDate, setTaxDrillDate] = useState<string | null>(null);
  const taxesAnchorRef = useRef<HTMLDivElement>(null);
  const setTopBarProps = useDesktopTopBar();
  const navigate = useNavigate();

  /*
   * REF-2 — the "Total Refunds" KPI opens the ONE shared Refunds drill-in instead of this
   * report's own retired refund drill. Every other drillKey keeps the existing in-page view.
   */
  const handleDrill = useCallback((key: string) => {
    if (key === 'refunds') {
      navigate(refundsPath({
        origin: 'eod',
        dateFilter: datePeriod,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        source: orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall',
      }));
      return;
    }
    setDrillView(key);
  }, [navigate, datePeriod, dateRange, orderSource]);

  const handleTaxDrillBack = useCallback(() => {
    setTaxDrillDate(null);
    setTimeout(() => {
      if (taxesAnchorRef.current) {
        taxesAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  }, []);

  useEffect(() => {
    const isAnyDrill = drillView || taxDrillDate;
    setTopBarProps({
      breadcrumbs: (drillView || taxDrillDate) ? "Reports › EOD Summary" : "Reports",
      title: taxDrillDate
        ? DRILL_TITLES_DESKTOP.taxDiscrepancy
        : drillView ? DRILL_TITLES_DESKTOP[drillView] ?? "EOD Summary" : "EOD Summary",
      dateFilters: isAnyDrill ? undefined : { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_EOD },
      // Sits beside the date control. Hidden inside every drill, exactly as `dateFilters`
      // Kept inside drills (unlike `dateFilters` above) so a drill can be re-scoped in
      // place — see CHANNEL_HIDDEN_DRILLS for the drills that opt out and why. The Tax
      // Discrepancy drill is tracked by `taxDrillDate` rather than `drillView`, so it is
      // resolved to its key here to be matched against the same list mobile uses.
      sourceFilters: CHANNEL_HIDDEN_DRILLS.includes(taxDrillDate ? 'taxDiscrepancy' : (drillView ?? ''))
        ? undefined
        : { value: channel, onChange: setChannel, options: CHANNEL_OPTIONS },
      // ER 15 — matches Snapshot's / Sales' own leftAddon byte-for-byte (28×28, radius 6,
      // T.hover fill, arrow_back at 16px in T.t2, marginRight 4) instead of EOD's own
      // 18px/8px CSS-class variant, and now covers the Tax Discrepancy drill too — it used
      // to render its own separately-styled "Back to EOD Summary" text button inside
      // TaxDiscrepancyDrillContent instead of using this shared control, which was the
      // other half of the inconsistency (two different back buttons within EOD itself).
      leftAddon: (drillView || taxDrillDate) ? (
        <div
          onClick={taxDrillDate ? handleTaxDrillBack : () => setDrillView(null)}
          style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: 4 }}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
        </div>
      ) : undefined,
    });
    return () => setTopBarProps(null);
  }, [setTopBarProps, datePeriod, setDatePeriod, channel, setChannel, drillView, taxDrillDate, handleTaxDrillBack]);

  if (!locationId) {
    return (
      <div className="eod-state-message eod-state-message--muted">
        Select a location to view EOD Summary.
      </div>
    );
  }

  // ER 17 — drill checks moved ABOVE the full-page loading/error branches. Changing the
  // global channel filter while inside a drill re-triggers useEodSummaryData, which flips
  // `loading` back to true; checking it first meant that refetch replaced the drill (Tender
  // Mix / Channel Revenue / Refunds / Tips) with the unrelated main-page EODDesktopShimmer
  // instead of a shimmer scoped to the drill's own content. Both drills now get `loading`
  // passed in and shimmer only their own KPI cards — TaxDiscrepancyDrillContent manages its
  // own separate fetch/loading already and doesn't follow the channel filter at all
  // (CHANNEL_HIDDEN_DRILLS), so it never needed this prop.
  if (taxDrillDate) {
    return (
      <div className="eod-desktop-drill-wrapper">
        <TaxDiscrepancyDrillContent date={taxDrillDate} />
      </div>
    );
  }

  if (drillView) {
    return (
      <div className="eod-desktop-drill-wrapper">
        <DesktopDrillContent view={drillView} data={data} loading={loading} onBack={() => setDrillView(null)} dateRange={dateRange} isToday={isToday} orderSource={orderSource} />
      </div>
    );
  }

  if (loading) {
    return <EODDesktopShimmer />;
  }
  if (error) {
    return (
      <div className="eod-state-message eod-state-message--error">
        {error}
      </div>
    );
  }

  return (
    <div className="eod-desktop-content-wrapper">
      <EODContent
        data={data} onDrill={handleDrill} onTaxDrill={setTaxDrillDate} taxesRef={taxesAnchorRef}
        datePeriod={datePeriod} orderSource={orderSource} salesType={salesType} setSalesType={setSalesType}
        taxesPage={taxesPage} setTaxesPage={setTaxesPage}
        taxesSize={taxesSize} setTaxesSize={setTaxesSize}
        taxesSizeChosen={taxesSizeChosen} setTaxesSizeChosen={setTaxesSizeChosen}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE LAYOUT (TopBar + content + shared bottom nav)
   ═══════════════════════════════════════════════════════════════════════════ */

function MOverviewContent({
  onDrill, onTaxDrill, taxesRef, data, datePeriod, orderSource, salesType, setSalesType,
  taxesPage, setTaxesPage, taxesSize, setTaxesSize, taxesSizeChosen, setTaxesSizeChosen,
}: any) {
  return (
    <div className="eod-mobile-overview">
      <EODContent
        onDrill={onDrill} onTaxDrill={onTaxDrill} taxesRef={taxesRef} data={data}
        datePeriod={datePeriod} orderSource={orderSource} salesType={salesType} setSalesType={setSalesType}
        taxesPage={taxesPage} setTaxesPage={setTaxesPage}
        taxesSize={taxesSize} setTaxesSize={setTaxesSize}
        taxesSizeChosen={taxesSizeChosen} setTaxesSizeChosen={setTaxesSizeChosen}
      />
    </div>
  );
}

function MTenderDrill({ tenderData: tenderListRaw, dateRange, isToday, orderSource, loading }: any) {
  const exportDateRangeLabel = exportPeriodFor(isToday ? 'Today' : undefined, isToday ? null : dateRange)?.label ?? 'Today';
  const list = normalizeTenderPercentages(tenderListRaw ?? []);
  const hasBreakdown = list.length && list.some((r: any) => r.totalSales != null || r.orders != null);
  const totalCollected = hasBreakdown ? list.reduce((s: number, r: any) => s + (Number(r.totalSales) || 0), 0) : 0;
  const { auth } = useAuth();
  const authToken = useMemo(() => (auth ? findAuthToken(auth) : null), [auth]);
  const buildTenderExportRows = (rows: any[]) => rows.map((r: any) => {
    const orders = Number(r.orders) || 0;
    const sales = Number(r.totalSales) || 0;
    const avgTicket = orders > 0 ? sales / orders : 0;
    return [
      r.name,
      orders > 0 ? orders : '—',
      sales > 0 ? formatMoneyForExport(sales) : '—',
      avgTicket > 0 ? formatMoneyForExport(avgTicket) : '—',
      `${Number(r.value).toFixed(2)}%`,
    ];
  });
  return (
    <div className="eod-mobile-drill">
      <div className="eod-mobile-drill__kpi-row">
        <KPICard label="Total Collected" value={totalCollected > 0 ? fmt(totalCollected) : "—"} loading={loading} />
        {hasBreakdown && list.slice(0, 3).map((t: any) => (
          <KPICard key={t.name} label={t.name} value={fmt(t.totalSales ?? 0)} loading={loading} />
        ))}
      </div>
      <ReportCard
        title="Tender Type Breakdown"
        subtitle="Detailed sales by payment method"
        icon="list_alt"
        style={{ marginBottom: 8 }}
        action={hasBreakdown ? (
          <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
            variant="mobile"
            title="Tender Type Breakdown"
            filename="eod-tender-type-breakdown"
            buildExport={async (_format, opts) => {
              const rows = opts?.locationId
                ? await fetchTenderListForOutlet(opts.locationId, authToken, dateRange, !!isToday, orderSource)
                : list;
              return {
                headers: ['Tender type', 'Transactions', 'Total sales', 'Avg ticket', '% of total'],
                rows: buildTenderExportRows(rows),
              };
            }}
          />
        ) : undefined}
      >
        {loading ? (
          <div>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i < 3 ? `1px solid ${T.divider}` : 'none' }}>
                <Bone w={'50%' as any} h={12} r={4} mb={6} />
                <Bone w={null} h={10} r={4} mb={4} />
                <Bone w={'70%' as any} h={10} r={4} />
              </div>
            ))}
          </div>
        ) : hasBreakdown ? (
          list.map((r: any) => {
            const orders = Number(r.orders) || 0;
            const sales = Number(r.totalSales) || 0;
            const avgTicket = orders > 0 ? sales / orders : 0;
            return (
              <div key={r.name} className="eod-mobile-tender__item">
                <div className="eod-mobile-tender__item-name">{r.name}</div>
                <div className="eod-mobile-tender__item-row">
                  <span>{orders > 0 ? orders.toLocaleString() + " orders" : "—"}</span>
                  <span>{sales > 0 ? fmt(sales) : "—"}</span>
                </div>
                <div className="eod-mobile-tender__item-bottom">
                  <span>Avg: {avgTicket > 0 ? getCurrencySymbol() + avgTicket.toFixed(2) : "—"}</span>
                  <span className="eod-mobile-tender__pct">{Number(r.value).toFixed(2)}%</span>
                </div>
              </div>
            );
          })
        ) : list.length > 0 ? (
          list.map((r: any, i: number) => (
            <div key={r.name} className={`eod-mobile-tender-list__item ${i < list.length - 1 ? "eod-mobile-tender-list__item--bordered" : ""}`}>
              <Icon name="credit_card" size={16} color={T.accent} style={{ marginRight: 8 }} />
              <div style={{ flex: 1 }}>
                <div className="eod-mobile-tender-list__name">{r.name}</div>
                <div className="eod-mobile-tender-list__orders">{r.orders != null ? r.orders + " orders" : "—"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="eod-mobile-tender-list__sales">{r.totalSales != null ? fmt(r.totalSales) : "—"}</div>
                <div className="eod-mobile-tender-list__pct">{r.value != null ? `${Number(r.value).toFixed(2)}%` : "—"}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="eod-no-data eod-no-data--sm">No tender data for this period.</div>
        )}
      </ReportCard>
    </div>
  );
}

/**
 * ZAR-1762 / TG-2 - mobile "Tips & Gratuity" drill-in for EOD Reconciliation.
 * Same shared two-section component as desktop; no source toggle (EOD is direct-only).
 */
function MTipsDrill({ data }: any) {
  return (
    <div className="eod-mobile-drill">
      <TipsGratuityDrill data={buildEodTipsGratuityData(data)} fmt={fmtTgMoney} isMobile />
    </div>
  );
}

function MRefundsDrill({ refundsSummary: rs, refundReasons: reasonsProp, loading }: any) {
  const reasons = reasonsProp ?? [];
  const refundVal = rs?.refundAmount != null ? fmt(rs.refundAmount) : "—";
  const refundRateVal = rs?.refundRate != null ? `${rs.refundRate.toFixed(1)}%` : "—";
  return (
    <div className="eod-mobile-drill">
      <div className="eod-mobile-drill__kpi-row">
        <KPICard label="Refunds" value={refundVal} loading={loading} />
        <KPICard label="Refund Rate" value={refundRateVal} loading={loading} />
      </div>
      <ReportCard title="Refund Reasons" icon="help_outline" style={{ marginBottom: 8 }}>
        {loading ? (
          <div>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <Bone w={90} h={11} r={4} />
                  <Bone w={54} h={11} r={4} />
                </div>
                <Bone w={null} h={5} r={3} />
              </div>
            ))}
          </div>
        ) : reasons.length ? reasons.map((r: any) => (
          <div key={r.reason} className="eod-refund-reason">
            <div className="eod-refund-reason__header">
              <span className="eod-mobile-refund-reason__name" style={{ color: T.t2 }}>{r.reason}</span>
              <span className="eod-mobile-refund-reason__value" style={{ color: T.t1 }}>
                {r.amount != null ? `${fmt(Number(r.amount) || 0)} (${r.pct}%)` : `${r.pct}%`}
              </span>
            </div>
            <div className="eod-refund-reason__bar eod-mobile-refund-reason__bar">
              <div className="eod-refund-reason__bar-fill" style={{ width: `${r.pct}%`, background: r.color || T.accent }} />
            </div>
          </div>
        )) : (
          <SectionEmpty kpiName="refund reasons" />
        )}
      </ReportCard>
    </div>
  );
}

function MChannelsDrill({ channelData: channelDataProp, channelSummary: cs, loading }: any) {
  const chData = channelDataProp ?? [];
  const drillPeak = buildChannelPeakLabel(chData, { stacked: true, fontSize: 8 });
  // EODCH-CHANNEL — mirrors the desktop drill above, card for card and formatter for formatter.
  const totalSalesVal = cs?.totalSales != null ? fmtMoney2(cs.totalSales) : (cs?.totalMaghil != null || cs?.totalThirdParty != null ? fmtMoney2((cs?.totalMaghil ?? 0) + (cs?.totalThirdParty ?? 0)) : "—");
  const maghilVal = cs?.totalMaghil != null ? fmtMoney2(cs.totalMaghil) : "—";
  const thirdPartyVal = cs?.totalThirdParty != null ? fmtMoney2(cs.totalThirdParty) : "—";
  const maghilPctVal = cs?.maghilPct != null ? `${cs.maghilPct.toFixed(1)}%` : "—";
  return (
    <div className="eod-mobile-drill">
      <div className="eod-mobile-drill__kpi-row">
        <KPICard label="Total Sales" value={totalSalesVal} loading={loading} />
        <KPICard label="Brisque" value={maghilVal} loading={loading} />
        <KPICard label="Third Party" value={thirdPartyVal} loading={loading} />
        <KPICard label="Brisque %" value={maghilPctVal} loading={loading} />
      </div>
      <ReportCard title="Revenue by Channel" icon="bar_chart" style={{ marginBottom: 8 }}>
        {loading ? (
          <Bone h={200} r={8} />
        ) : chData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chData} margin={{ top: 19, right: 0, left: -20, bottom: 24 }}>
              <XAxis
                dataKey="channel"
                interval={0}
                tick={{ fontSize: 8, fill: T.t3 }}
                tickMargin={6}
                angle={-35}
                textAnchor="end"
                height={40}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 7, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={channelYAxisTick} />
              <Tooltip content={<ChannelRevenueChartTooltip />} cursor={false} />
              <Bar dataKey="maghil" name="Brisque" stackId="a" fill={T.accent} maxBarSize={18}>
                <LabelList valueAccessor={drillPeak.valueAccessor('maghil')} content={drillPeak.content} />
              </Bar>
              <Bar dataKey="thirdParty" name="Third Party" stackId="a" fill={T.orange} radius={[3, 3, 0, 0]} maxBarSize={18}>
                <LabelList valueAccessor={drillPeak.valueAccessor('thirdParty')} content={drillPeak.content} />
              </Bar>
              <Legend verticalAlign="bottom" align="center" iconType="square" iconSize={9} wrapperStyle={{ fontSize: 10, color: T.t2 }} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="eod-no-data eod-no-data--sm">No channel data for this period.</div>
        )}
      </ReportCard>
    </div>
  );
}

const DRILL_TITLES: any = { overview: "EOD Summary", tender: "Tender Types", tips: TG_DRILL_TITLE, refunds: "Refunds", channels: "Channels", taxDiscrepancy: "Tax Discrepancy Audit Log" };

function MobileLayout({
  datePeriod, setDatePeriod, channel, setChannel, orderSource, salesType, setSalesType,
  data, loading, error, locationId, refetch, dateRange, isToday,
  taxesPage, setTaxesPage, taxesSize, setTaxesSize, taxesSizeChosen, setTaxesSizeChosen,
}: any) {
  const setTopBarProps = useDesktopTopBar();
  const [screen, setScreen] = useState("overview");
  const [taxDrillDate, setTaxDrillDate] = useState<string | null>(null);
  const taxesAnchorRef = useRef<HTMLDivElement>(null);
  const isDrill = screen !== "overview";
  const navigate = useNavigate();

  /* REF-2 — same redirect as desktop: "Total Refunds" opens the shared drill-in. */
  const handleDrill = useCallback((key: string) => {
    if (key === 'refunds') {
      navigate(refundsPath({
        origin: 'eod',
        dateFilter: datePeriod,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        source: orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall',
      }));
      return;
    }
    setScreen(key);
  }, [navigate, datePeriod, dateRange, orderSource]);
  const handlePtrRefresh = useCallback(() => {
    refetch?.();
  }, [refetch]);
  const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

  useDrillHistoryBack(isDrill, () => { setScreen("overview"); setTaxDrillDate(null); });

  const handleTaxDrill = useCallback((date: string) => {
    setTaxDrillDate(date);
    setScreen('taxDiscrepancy');
  }, []);

  const handleTaxDrillMobileBack = useCallback(() => {
    setScreen('overview');
    setTaxDrillDate(null);
    setTimeout(() => {
      if (taxesAnchorRef.current) {
        taxesAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  }, []);

  useSwipeBack();

  useEffect(() => {
    setTopBarProps({
      title: isDrill ? DRILL_TITLES[screen] ?? "EOD Summary" : "EOD Summary",
      dateFilters: isDrill ? undefined : { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_EOD },
      // Same rule as desktop — one unified screen, so the control appears and hides in the
      // same places on both breakpoints.
      sourceFilters: CHANNEL_HIDDEN_DRILLS.includes(screen)
        ? undefined
        : { value: channel, onChange: setChannel, options: CHANNEL_OPTIONS },
      onBack: isDrill ? (screen === 'taxDiscrepancy' ? handleTaxDrillMobileBack : () => setScreen("overview")) : undefined,
    });
  }, [setTopBarProps, screen, isDrill, datePeriod, setDatePeriod, channel, setChannel, handleTaxDrillMobileBack]);

  const drillContent: any = {
    tender: <MTenderDrill tenderData={data?.tenderData} dateRange={dateRange} isToday={isToday} orderSource={orderSource} loading={loading} />,
    tips: <MTipsDrill data={data} />,
    refunds: <MRefundsDrill refundsSummary={data?.refundsSummary} refundReasons={data?.refundReasons} loading={loading} />,
    channels: <MChannelsDrill channelData={data?.channelData} channelSummary={data?.channelSummary} loading={loading} />,
    // ER 17 — TaxDiscrepancyDrillContent manages its own separate fetch/loading and never
    // follows the global channel filter (CHANNEL_HIDDEN_DRILLS), so it doesn't take `loading`.
    taxDiscrepancy: taxDrillDate ? <TaxDiscrepancyDrillContent date={taxDrillDate} /> : null,
  };

  // ER 17 — a drill screen renders regardless of `loading`/`error` now (each drill above gets
  // `loading` and shimmers only its own KPI row), instead of being gated behind `showContent`
  // the same as the overview. Before this, changing the global channel filter while inside
  // Tender/Refunds/Channels flipped `loading` true, `showContent` false, and swapped the
  // whole drill out for the unrelated main-page EODMobileShimmer.
  const isDrillScreen = screen !== "overview";
  const overviewProps = {
    onDrill: handleDrill, onTaxDrill: handleTaxDrill, taxesRef: taxesAnchorRef, data,
    datePeriod, orderSource, salesType, setSalesType,
    taxesPage, setTaxesPage, taxesSize, setTaxesSize, taxesSizeChosen, setTaxesSizeChosen,
  };
  return (
    <div className="eod-mobile-root">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
      <TopBar variant="mobile" />
      <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto' }}>
        {!locationId && (
          <div className="eod-state-message eod-state-message--muted eod-state-message--sm">Select a location to view EOD Summary.</div>
        )}
        {locationId && isDrillScreen && (drillContent[screen] || <MOverviewContent {...overviewProps} />)}
        {locationId && !isDrillScreen && loading && <EODMobileShimmer />}
        {locationId && !isDrillScreen && !loading && error && (
          <div className="eod-state-message eod-state-message--error eod-state-message--sm">{error}</div>
        )}
        {locationId && !isDrillScreen && !loading && !error && <MOverviewContent {...overviewProps} />}
      </div>
      <BottomNav />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function EODSummaryReport() {
  const isDesktop = useIsDesktop();
  // EODCH-TODAY — `excludeToday` rejects a "Today" value carried over from another report's
  // shared date filter (Snapshot/Sales both have a Today pill; EOD deliberately doesn't —
  // DATE_PRESETS_EOD omits it). Without this, `useSharedReportDateFilter` treated a carried
  // "Today" as valid on ANY page whose resolver understands the token, regardless of whether
  // that page's own preset list offers it — landing EOD on a period it has no pill for
  // instead of falling back to "Yesterday" here.
  const [datePeriod, setDatePeriod] = useSharedReportDateFilter("Yesterday", { excludeToday: true, options: DATE_PRESETS_EOD });
  // Per-report channel state — NOT shared with Snapshot or Sales, defaults to Overall on a
  // fresh visit, and held here (above the desktop/mobile branch) so both layouts, the hook
  // and every section below read the one selection.
  const [channel, setChannel] = useState('Overall sales');
  const orderSource = channelToOrderSource(channel);
  /**
   * The Summary Report table's Direct / 3rd Party column set, lifted OUT of that table.
   *
   * It cannot live in `SummaryReportTable`: both layouts return a shimmer instead of the
   * content while `loading` is true (`if (loading) return <EODDesktopShimmer />` on
   * desktop, `showContent` on mobile), so every refetch UNMOUNTS the whole content
   * subtree and the table comes back with its `useState` default. Held here — above both
   * early returns — it survives.
   *
   * That reset was visible long before the channel filter: any date change, outlet switch
   * or pull-to-refresh already snapped the radio back to Direct. It only became obvious
   * when "Overall sales" started triggering a refetch, because Overall is the one channel
   * option whose sync is deliberately a no-op, so nothing re-applied the value afterwards
   * and the bare default showed through.
   */
  const [summarySalesType, setSummarySalesType] = useState<'direct' | 'thirdParty'>('direct');
  /**
   * ER 8 — the Taxes table's page / rows-per-page, lifted OUT of `TaxesTableSection` for the
   * same reason `summarySalesType` above is lifted out of `SummaryReportTable`: clicking a
   * discrepancy row opens the Tax Discrepancy Audit Log by swapping the whole `EODContent`
   * subtree for `TaxDiscrepancyDrillContent` (see `taxDrillDate` in `EODDesktopContent` /
   * `MobileLayout`), so "Back to EOD Summary" remounts `TaxesTableSection` fresh and its own
   * `useState` was coming back at page 1 instead of the page the user drilled in from.
   * `taxesSizeChosen` mirrors the table's old `sizeChosenRef`: it stops the viewport-
   * responsive size default from re-applying over an explicit user pick, and has to be
   * lifted alongside page/size for the same reason — a fresh `useRef` would come back
   * `false` on every remount and immediately stomp the restored size.
   */
  const [taxesPage, setTaxesPage] = useState(0);
  const [taxesSize, setTaxesSize] = useState(isDesktop ? EOD_PAGE_SIZE_DESKTOP : EOD_PAGE_SIZE_MOBILE);
  const [taxesSizeChosen, setTaxesSizeChosen] = useState(false);
  // ER 8 — the ONE place the Taxes table's page must reset on a date change: passed down
  // instead of the raw `setDatePeriod` as the TopBar date picker's `onChange`, so the reset
  // happens as a direct, one-shot consequence of the user actually picking a new range —
  // never as a side effect a later remount (e.g. returning from the Tax Discrepancy Audit
  // Log) could replay. See the removed effect's comment in TaxesTableSection for why a
  // reactive `useEffect` keyed on the resolved date range could not be made to tell "mount"
  // apart from "real change" reliably.
  const handleDatePeriodChange = useCallback((value: string) => {
    setDatePeriod(value);
    setTaxesPage(0);
  }, [setDatePeriod]);
  const { data, loading, error, locationId, refetch, dateRange, isToday } = useEodSummaryData(datePeriod, orderSource);

  if (isDesktop) {
    return (
      <DesktopLayout>
        <EODDesktopContent
          datePeriod={datePeriod}
          setDatePeriod={handleDatePeriodChange}
          channel={channel}
          setChannel={setChannel}
          orderSource={orderSource}
          salesType={summarySalesType}
          setSalesType={setSummarySalesType}
          data={data}
          loading={loading}
          error={error}
          locationId={locationId}
          dateRange={dateRange}
          isToday={isToday}
          taxesPage={taxesPage}
          setTaxesPage={setTaxesPage}
          taxesSize={taxesSize}
          setTaxesSize={setTaxesSize}
          taxesSizeChosen={taxesSizeChosen}
          setTaxesSizeChosen={setTaxesSizeChosen}
        />
      </DesktopLayout>
    );
  }
  return (
    <MobileLayout
      datePeriod={datePeriod}
      setDatePeriod={handleDatePeriodChange}
      channel={channel}
      setChannel={setChannel}
      orderSource={orderSource}
      salesType={summarySalesType}
      setSalesType={setSummarySalesType}
      data={data}
      loading={loading}
      error={error}
      locationId={locationId}
      refetch={refetch}
      dateRange={dateRange}
      isToday={isToday}
      taxesPage={taxesPage}
      setTaxesPage={setTaxesPage}
      taxesSize={taxesSize}
      setTaxesSize={setTaxesSize}
      taxesSizeChosen={taxesSizeChosen}
      setTaxesSizeChosen={setTaxesSizeChosen}
    />
  );
}
